import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import './FulfillmentHubPage.css';

export default function FulfillmentHubPage() {
  const { currentUser, isAdmin, canPerformAction } = useAuth();

  // Test / Simulation Mode (Dry-Run)
  const [isTestMode, setIsTestMode] = useState(() => {
    const saved = localStorage.getItem('fvs_fulfillment_test_mode');
    return saved !== null ? saved === 'true' : true; // Default to test mode for initial safety
  });

  const getAuthHeaders = useCallback(
    async (extraHeaders = {}) => {
      if (!currentUser) return extraHeaders;
      const token = await currentUser.getIdToken();
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-fulfillment-mode': isTestMode ? 'test' : 'live',
        ...extraHeaders,
      };
    },
    [currentUser, isTestMode]
  );

  // Normalize Shopify orders to have '00' suffix (e.g. 70005 -> 7000500)
  const formatOrderNo = (orderNumber, source = '', isCustom = false) => {
    if (!orderNumber) return '';
    const clean = String(orderNumber).trim().replace(/^#/, '');
    const isCustomOrder = isCustom === true ||
      String(source || '').toLowerCase().includes('custom') ||
      String(source || '').toLowerCase().includes('manual') ||
      clean.toUpperCase().startsWith('CUST-') ||
      clean.toUpperCase().startsWith('CO-') ||
      clean.toUpperCase().startsWith('CUSTOM');

    if (isCustomOrder) return clean;
    if (/^\d+$/.test(clean) && !clean.endsWith('00')) {
      return `${clean}00`;
    }
    return clean;
  };

  // Active Station Tab: 'picking' | 'production' | 'packing' | 'ndr' | 'returns' | 'outbox'
  const [activeTab, setActiveTab] = useState('picking');

  // Global error / feedback
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [zoomImage, setZoomImage] = useState(null); // { url, sku, title }

  // ── Station 2 State (Picking & Packaging Desk) ──────────────────────────
  const [pickData, setPickData] = useState({ pickList: [], totalSkus: 0, totalPiecesToPick: 0, totalDeficitPieces: 0 });
  const [isPickLoading, setIsPickLoading] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [pickedItems, setPickedItems] = useState({});
  const [pickFilterTab, setPickFilterTab] = useState('ready'); // 'ready' | 'deficit' | 'all'
  const [pickViewMode, setPickViewMode] = useState('grid'); // 'grid' | 'table'
  const [selectedWaitingOrdersItem, setSelectedWaitingOrdersItem] = useState(null);

  // SKU Piece Allocation Studio state
  const [selectedAllocationSku, setSelectedAllocationSku] = useState(null);
  const [selectedAllocationOrder, setSelectedAllocationOrder] = useState(null);
  const [skuPieces, setSkuPieces] = useState([]);
  const [isLoadingPieces, setIsLoadingPieces] = useState(false);
  const [showShortRolls, setShowShortRolls] = useState(false);
  const [selectedPieceId, setSelectedPieceId] = useState('');
  const [multiPieceSelection, setMultiPieceSelection] = useState({});
  const [isAllocatingPiece, setIsAllocatingPiece] = useState(false);
  const [allocationFeedback, setAllocationFeedback] = useState(null);

  // Packaging Desk Quick Lock state
  const [orderBasketPreview, setOrderBasketPreview] = useState(null);
  const [isBasketLoading, setIsBasketLoading] = useState(false);

  // ── Station 3 State (Production Batches & QC) ───────────────────────────
  const [prodSubTab, setProdSubTab] = useState('deficit');
  const [deficitData, setDeficitData] = useState({ items: [], totalDeficitSkus: 0, totalDeficitPieces: 0 });
  const [isDeficitLoading, setIsDeficitLoading] = useState(false);
  const [selectedDeficitSkus, setSelectedDeficitSkus] = useState({});
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [batchDeliveryDate, setBatchDeliveryDate] = useState('');
  const [batchNotes, setBatchNotes] = useState('');
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);

  // Inbound QC Receiving State
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [qcSku, setQcSku] = useState('');
  const [qcPieceBarcode, setQcPieceBarcode] = useState('');
  const [qcMeasuredLength, setQcMeasuredLength] = useState('');
  const [qcDamagedMeters, setQcDamagedMeters] = useState('');
  const [qcShelfLocation, setQcShelfLocation] = useState('Shelf B-2 (Finished Goods)');
  const [qcGrade, setQcGrade] = useState('GRADE_A');
  const [qcNotes, setQcNotes] = useState('');
  const [isReceivingPiece, setIsReceivingPiece] = useState(false);
  const [routingFeedback, setRoutingFeedback] = useState(null);
  const [selectedDeficitWaitingOrders, setSelectedDeficitWaitingOrders] = useState(null);

  // ── Station 4 State (Packing Station & Courier Handover) ────────────────
  const [packingQueue, setPackingQueue] = useState({ readyOrders: [], packedOrders: [], totalReadyToPack: 0, totalPackedReadyToDispatch: 0 });
  const [isPackingLoading, setIsPackingLoading] = useState(false);
  const [packingForms, setPackingForms] = useState({});
  const [actionInProgress, setActionInProgress] = useState({});

  // ── Station 6 State (NDR Action Center) ─────────────────────────────────
  const [ndrQueue, setNdrQueue] = useState({ ndrOrders: [], totalNdrCount: 0 });
  const [isNdrLoading, setIsNdrLoading] = useState(false);
  const [reattemptModalOrder, setReattemptModalOrder] = useState(null);
  const [reattemptDate, setReattemptDate] = useState('');
  const [reattemptRemarks, setReattemptRemarks] = useState('');
  const [isSubmittingReattempt, setIsSubmittingReattempt] = useState(false);

  // ── Station 7 State (Returns, QC & Exchanges) ───────────────────────────
  const [returnsQueue, setReturnsQueue] = useState({ returns: [], totalInboundReturns: 0 });
  const [isReturnsLoading, setIsReturnsLoading] = useState(false);
  const [selectedReturnOrder, setSelectedReturnOrder] = useState(null);
  const [returnQcSku, setReturnQcSku] = useState('');
  const [returnQcMeasuredLength, setReturnQcMeasuredLength] = useState('');
  const [returnQcDamagedMeters, setReturnQcDamagedMeters] = useState('');
  const [returnQcShelfLocation, setReturnQcShelfLocation] = useState('Shelf A-1 (Restocked Returns)');
  const [returnQcGrade, setReturnQcGrade] = useState('GRADE_A');
  const [returnQcNotes, setReturnQcNotes] = useState('');
  const [isSubmittingReturnQc, setIsSubmittingReturnQc] = useState(false);

  // 1-Click Exchange modal state
  const [exchangeModalOrder, setExchangeModalOrder] = useState(null);
  const [exchangeSku, setExchangeSku] = useState('');
  const [exchangeReason, setExchangeReason] = useState('Customer Size / Color Exchange');
  const [isSubmittingExchange, setIsSubmittingExchange] = useState(false);

  // ── Station 5 State (Transactional Outbox Monitor) ──────────────────────
  const [outboxJobs, setOutboxJobs] = useState([]);
  const [isOutboxLoading, setIsOutboxLoading] = useState(false);
  const [isProcessingOutbox, setIsProcessingOutbox] = useState(false);

  // ── Phase 5 State (Zero-Error Pipeline Health & Nightly Audit) ───────────
  const [auditReport, setAuditReport] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [isRunningAudit, setIsRunningAudit] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);

  // Permissions helpers
  const canPick = canPerformAction('fulfillment.pick') || canPerformAction('fulfillment.view');
  const canMarkUsed = canPerformAction('fulfillment.markUsed') || canPerformAction('orders.markUsed');
  const canCreateBatch = canPerformAction('fulfillment.createBatch') || canPerformAction('batches.create');
  const canReceiveQC = canPerformAction('fulfillment.receiveBatch') || canPerformAction('batches.receive');
  const canGenerateLabel = canPerformAction('fulfillment.generateLabel') || canPerformAction('fulfillment.pack');
  const canDispatch = canPerformAction('fulfillment.dispatch') || canPerformAction('orders.ship');
  const canHandleNdr = canPerformAction('fulfillment.handleNDR') || canPerformAction('fulfillment.view');
  const canProcessReturns = canPerformAction('fulfillment.processReturns') || canPerformAction('fulfillment.view');
  const canCreateExchange = canPerformAction('fulfillment.createExchange');

  // ── Data Fetching Handlers ──────────────────────────────────────────────

  const fetchPickList = useCallback(async () => {
    if (!currentUser) return;
    setIsPickLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/fulfillment/pick-list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch pick list.');
      setPickData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsPickLoading(false);
    }
  }, [currentUser]);

  const fetchDeficitItems = useCallback(async () => {
    if (!currentUser) return;
    setIsDeficitLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/fulfillment/deficit-items', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch deficit items.');
      setDeficitData(data);
      const initialSelected = {};
      (data.items || []).forEach((it) => {
        if (it.netDeficit > 0) initialSelected[it.sku] = true;
      });
      setSelectedDeficitSkus(initialSelected);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDeficitLoading(false);
    }
  }, [currentUser]);

  const fetchBatches = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/fulfillment/batches', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setBatches(data.batches || []);
        if (data.batches?.length > 0 && !selectedBatchId) {
          setSelectedBatchId(data.batches[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load batches:', err);
    }
  }, [currentUser, selectedBatchId]);

  const fetchPackingQueue = useCallback(async () => {
    if (!currentUser) return;
    setIsPackingLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/fulfillment/packing-queue', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch packing queue.');
      setPackingQueue(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsPackingLoading(false);
    }
  }, [currentUser]);

  const fetchNdrQueue = useCallback(async () => {
    if (!currentUser) return;
    setIsNdrLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/fulfillment/ndr-queue', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch NDR queue.');
      setNdrQueue(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsNdrLoading(false);
    }
  }, [currentUser]);

  const fetchReturnsQueue = useCallback(async () => {
    if (!currentUser) return;
    setIsReturnsLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/fulfillment/returns-queue', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch returns queue.');
      setReturnsQueue(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsReturnsLoading(false);
    }
  }, [currentUser]);

  const fetchOutboxJobs = useCallback(async () => {
    if (!currentUser) return;
    setIsOutboxLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/fulfillment/outbox/jobs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setOutboxJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Failed to load outbox jobs:', err);
    } finally {
      setIsOutboxLoading(false);
    }
  }, [currentUser]);

  const fetchAuditLogs = useCallback(async () => {
    if (!currentUser) return;
    setIsAuditLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/fulfillment/audit/logs?limit=10', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setAuditLogs(data.logs || []);
        if (data.logs?.length > 0 && !auditReport) {
          setAuditReport(data.logs[0]);
        }
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setIsAuditLoading(false);
    }
  }, [currentUser, auditReport]);

  const handleRunAudit = async () => {
    setIsRunningAudit(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/fulfillment/audit/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Audit execution failed.');
      setAuditReport(data.report);
      setSuccessMsg(`Zero-Error Parity Audit completed! Health Score: ${data.report.healthScore}%`);
      fetchAuditLogs();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRunningAudit(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'picking') {
      fetchPickList();
    } else if (activeTab === 'production') {
      fetchDeficitItems();
      fetchBatches();
    } else if (activeTab === 'packing') {
      fetchPackingQueue();
    } else if (activeTab === 'ndr') {
      fetchNdrQueue();
    } else if (activeTab === 'returns') {
      fetchReturnsQueue();
    } else if (activeTab === 'outbox') {
      fetchOutboxJobs();
    } else if (activeTab === 'audit') {
      fetchAuditLogs();
    }
  }, [activeTab, fetchPickList, fetchDeficitItems, fetchBatches, fetchPackingQueue, fetchNdrQueue, fetchReturnsQueue, fetchOutboxJobs, fetchAuditLogs]);

  // ── Station 2 Actions ───────────────────────────────────────────────────

  const fetchOrderBasketPreview = useCallback(async (orderIdToFetch) => {
    if (!orderIdToFetch || !currentUser) {
      setOrderBasketPreview(null);
      return;
    }
    setIsBasketLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`/api/v1/fulfillment/order-basket/${encodeURIComponent(orderIdToFetch.trim())}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.found) {
        setOrderBasketPreview(data);
      } else {
        setOrderBasketPreview(null);
      }
    } catch (err) {
      setOrderBasketPreview(null);
    } finally {
      setIsBasketLoading(false);
    }
  }, [currentUser]);

  const handleTogglePicked = (sku) => {
    setPickedItems((prev) => ({ ...prev, [sku]: !prev[sku] }));
  };

  const fetchSkuPieces = async (sku, targetOrder = null, fallbackPieces = []) => {
    if (!sku) return;
    setIsLoadingPieces(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/v1/fulfillment/sku-pieces/${encodeURIComponent(sku)}`, { headers });
      const data = await res.json();
      const finalPieces = (res.ok && Array.isArray(data.pieces) && data.pieces.length > 0)
        ? data.pieces
        : (fallbackPieces && fallbackPieces.length > 0 ? fallbackPieces : []);

      setSkuPieces(finalPieces);

      const activeOrd = targetOrder || selectedAllocationOrder;
      if (activeOrd && finalPieces.length > 0) {
        const orderQty = Number(activeOrd.quantity || 1);
        if (orderQty <= 15) {
          // Check for exact zero-waste match first
          const exact = finalPieces.find((p) => Math.abs(Number(p.length || 0) - orderQty) < 0.01);
          if (exact) {
            setSelectedPieceId(exact.pieceId);
          } else {
            const eligible = finalPieces.filter((p) => Number(p.length || 0) + 1e-9 >= orderQty);
            eligible.sort((a, b) => (Number(a.length || 0) - orderQty) - (Number(b.length || 0) - orderQty));
            if (eligible[0]?.pieceId) {
              setSelectedPieceId(eligible[0].pieceId);
            }
          }
        }
      }
    } catch (err) {
      console.warn('[FulfillmentHub] Failed to fetch sku pieces:', err);
      if (fallbackPieces && fallbackPieces.length > 0) {
        setSkuPieces(fallbackPieces);
      }
    } finally {
      setIsLoadingPieces(false);
    }
  };

  const handleOpenSkuAllocation = (item, specificOrder = null) => {
    setSelectedAllocationSku(item);
    setShowShortRolls(false);
    setAllocationFeedback(null);
    setMultiPieceSelection({});

    const initialPieces = Array.isArray(item.availablePieces) ? item.availablePieces : [];
    setSkuPieces(initialPieces);

    // Only include orders that can actually be fulfilled from available pieces on shelf
    const fulfillableOrders = (item.orders || []).filter((ord) => {
      const ordQty = Number(ord.quantity || 1);
      if (ordQty <= 15) {
        return initialPieces.some((p) => Number(p.length || 0) + 1e-9 >= ordQty);
      }
      const totalAvailable = initialPieces.reduce((s, p) => s + (Number(p.length) || 0), 0);
      return totalAvailable + 1e-9 >= ordQty;
    });

    // Two-Way Best Match: prioritize orders that have an EXACT MATCH piece first
    const ordersList = fulfillableOrders.length > 0 ? [...fulfillableOrders] : [...(item.orders || [])];
    ordersList.sort((a, b) => {
      const aQty = Number(a.quantity || 1);
      const bQty = Number(b.quantity || 1);
      const aHasExact = initialPieces.some((p) => Math.abs(Number(p.length || 0) - aQty) < 0.01);
      const bHasExact = initialPieces.some((p) => Math.abs(Number(p.length || 0) - bQty) < 0.01);
      if (aHasExact && !bHasExact) return -1;
      if (!aHasExact && bHasExact) return 1;
      return aQty - bQty;
    });

    const targetOrder = specificOrder || ordersList[0] || null;
    setSelectedAllocationOrder(targetOrder);

    if (targetOrder && initialPieces.length > 0) {
      const orderQty = Number(targetOrder.quantity || 1);
      if (orderQty <= 15) {
        const exact = initialPieces.find((p) => Math.abs(Number(p.length || 0) - orderQty) < 0.01);
        if (exact) {
          setSelectedPieceId(exact.pieceId);
        } else {
          const eligible = initialPieces.filter((p) => Number(p.length || 0) + 1e-9 >= orderQty);
          eligible.sort((a, b) => (Number(a.length || 0) - orderQty) - (Number(b.length || 0) - orderQty));
          setSelectedPieceId(eligible[0]?.pieceId || initialPieces[0]?.pieceId || '');
        }
      } else {
        setSelectedPieceId('');
      }
    } else {
      setSelectedPieceId('');
    }

    fetchSkuPieces(item.sku, targetOrder, initialPieces);
  };

  const handleSelectOrderInModal = (ord) => {
    setSelectedAllocationOrder(ord);
    setShowShortRolls(false);
    setAllocationFeedback(null);
    setMultiPieceSelection({});
    const orderQty = Number(ord.quantity || 1);
    if (orderQty <= 15 && skuPieces.length > 0) {
      const exact = skuPieces.find((p) => Math.abs(Number(p.length || 0) - orderQty) < 0.01);
      if (exact) {
        setSelectedPieceId(exact.pieceId);
      } else {
        const eligible = skuPieces.filter((p) => Number(p.length || 0) + 1e-9 >= orderQty);
        eligible.sort((a, b) => (Number(a.length || 0) - orderQty) - (Number(b.length || 0) - orderQty));
        setSelectedPieceId(eligible[0]?.pieceId || skuPieces[0]?.pieceId || '');
      }
    } else {
      setSelectedPieceId('');
    }
  };

  const handlePieceToggle = (piece) => {
    if (!selectedAllocationOrder) return;
    const orderQty = Number(selectedAllocationOrder.quantity || 1);
    const pieceId = String(piece.pieceId).trim();
    const pieceLen = Number(piece.length || 0);

    if (orderQty <= 15) {
      if (pieceLen + 1e-9 < orderQty) return;
      setSelectedPieceId(pieceId);
    } else {
      setMultiPieceSelection((prev) => {
        const next = { ...prev };
        if (Object.prototype.hasOwnProperty.call(next, pieceId)) {
          delete next[pieceId];
          return next;
        }
        const already = Object.values(next).reduce((s, v) => s + Number(v || 0), 0);
        const remaining = Math.max(0, orderQty - already);
        const prefill = Math.min(pieceLen, remaining > 0 ? remaining : pieceLen);
        next[pieceId] = String(prefill > 0 ? prefill : pieceLen);
        return next;
      });
    }
  };

  const handleMultiPieceQtyChange = (pieceId, rawValue) => {
    const numeric = Number(rawValue || 0);
    setMultiPieceSelection((prev) => ({
      ...prev,
      [pieceId]: Number.isFinite(numeric) && numeric >= 0 ? String(numeric) : '',
    }));
  };

  const handleConfirmSkuAllocation = async () => {
    if (!selectedAllocationSku || !selectedAllocationOrder) return;
    const orderId = selectedAllocationOrder.orderId || selectedAllocationOrder.orderNumber;
    const orderQty = Number(selectedAllocationOrder.quantity || 1);
    const sku = selectedAllocationSku.sku;

    let payload = {
      orderId: String(orderId).trim(),
      sku: sku.trim().toUpperCase(),
      lineItemId: selectedAllocationOrder.lineItemId || undefined,
      quantity: orderQty,
    };

    if (orderQty <= 15) {
      if (!selectedPieceId) {
        setError('Please select a piece from the shelf.');
        return;
      }
      payload.pieceId = selectedPieceId;
    } else {
      const selectedEntries = Object.entries(multiPieceSelection).filter(([_, qty]) => Number(qty) > 0);
      if (selectedEntries.length === 0) {
        setError('Please select at least one piece and specify cut meters.');
        return;
      }
      const totalSelected = selectedEntries.reduce((s, [_, qty]) => s + Number(qty), 0);
      if (Math.abs(totalSelected - orderQty) > 0.01) {
        setError(`Total selected cut meters (${totalSelected}m) must equal order required quantity (${orderQty}m).`);
        return;
      }
      payload.pieces = selectedEntries.map(([pieceId, qty]) => ({ pieceId, qty: Number(qty) }));
    }

    setIsAllocatingPiece(true);
    setError('');
    setAllocationFeedback(null);

    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/mark-used', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to allocate piece.');

      setAllocationFeedback({
        success: true,
        message: data.message || `Allocated ${orderQty}m to Order #${selectedAllocationOrder.orderNumber || orderId}.`,
        dryRun: data.dryRun,
      });

      // Update remaining orders list in modal
      const remainingOrders = (selectedAllocationSku.orders || []).filter(
        (o) => (o.orderId || o.orderNumber) !== (selectedAllocationOrder.orderId || selectedAllocationOrder.orderNumber)
      );

      setSelectedAllocationSku((prev) => ({
        ...prev,
        orders: remainingOrders,
        totalNeeded: Math.max(0, (prev.totalNeeded || 0) - orderQty),
      }));

      // If more orders exist for this SKU, pick next order
      if (remainingOrders.length > 0) {
        handleSelectOrderInModal(remainingOrders[0]);
      } else {
        setSelectedAllocationOrder(null);
      }

      fetchPickList();
      fetchSkuPieces(sku);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsAllocatingPiece(false);
    }
  };

  const handleUnallocateStock = async (orderId, sku, lineItemId) => {
    if (!orderId || !sku) return;
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/unallocate-piece', {
        method: 'POST',
        headers,
        body: JSON.stringify({ orderId, sku, lineItemId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to unallocate stock.');
      setSuccessMsg(data.message || `Unallocated ${sku} and returned to shelf.`);
      fetchPickList();
      if (orderBasketPreview?.orderId === orderId) {
        fetchOrderBasketPreview(orderId);
      }
      fetchPackingQueue();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Station 3 Actions ───────────────────────────────────────────────────

  const handleCreateBatchSubmit = async () => {
    const itemsToBatch = (deficitData.items || [])
      .filter((it) => selectedDeficitSkus[it.sku])
      .map((it) => ({
        sku: it.sku,
        title: it.title,
        quantity: it.netDeficit || it.totalNeeded,
        orders: it.orders,
      }));

    if (itemsToBatch.length === 0) {
      setError('Please select at least one SKU to compile into the batch.');
      return;
    }

    setIsCreatingBatch(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/batches', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: itemsToBatch,
          targetDeliveryDate: batchDeliveryDate || null,
          notes: batchNotes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to compile batch.');
      setSuccessMsg(data.message || `Successfully created Factory Batch #${data.batch?.batchNumber || ''}!`);
      setIsBatchModalOpen(false);
      setBatchNotes('');
      fetchDeficitItems();
      fetchBatches();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCreatingBatch(false);
    }
  };

  const handleReceivePieceSubmit = async (e) => {
    e?.preventDefault();
    if (!selectedBatchId || !qcSku) {
      setError('Please select a Production Batch and SKU.');
      return;
    }
    setIsReceivingPiece(true);
    setError('');
    setRoutingFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/v1/fulfillment/batches/${selectedBatchId}/receive-piece`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sku: qcSku.trim().toUpperCase(),
          pieceBarcode: qcPieceBarcode.trim() || null,
          measuredLength: Number(qcMeasuredLength || 0),
          damagedMeters: Number(qcDamagedMeters || 0),
          shelfLocation: qcShelfLocation.trim(),
          qcGrade,
          notes: qcNotes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to receive piece.');
      setRoutingFeedback(data);
      setQcPieceBarcode('');
      setQcDamagedMeters('');
      setQcNotes('');
      fetchBatches();
      fetchDeficitItems();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsReceivingPiece(false);
    }
  };

  // ── Station 4 Actions ───────────────────────────────────────────────────

  const handleFormChange = (orderId, field, value) => {
    setPackingForms((prev) => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || { grossWeightGrams: 450, length: 25, width: 20, height: 5, carrier: 'shiprocket' }),
        [field]: value,
      },
    }));
  };

  const handleGenerateLabel = async (orderId) => {
    const form = packingForms[orderId] || { grossWeightGrams: 450, length: 25, width: 20, height: 5, carrier: 'shiprocket' };
    setActionInProgress((prev) => ({ ...prev, [orderId]: 'label' }));
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/generate-label', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          orderId,
          grossWeightGrams: form.grossWeightGrams,
          boxDimensions: {
            length: form.length,
            width: form.width,
            height: form.height,
          },
          carrier: form.carrier,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to generate shipping label.');
      setSuccessMsg(data.message || `Generated carrier AWB ${data.awb} for Order #${data.orderNumber}!`);
      fetchPackingQueue();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionInProgress((prev) => ({ ...prev, [orderId]: null }));
    }
  };

  const handleDispatchOrder = async (orderId) => {
    setActionInProgress((prev) => ({ ...prev, [orderId]: 'dispatch' }));
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/dispatch', {
        method: 'POST',
        headers,
        body: JSON.stringify({ orderId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to dispatch order.');
      setSuccessMsg(data.message || `Order #${data.orderNumber} DISPATCHED! Zoho Invoicing outbox job triggered.`);
      fetchPackingQueue();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionInProgress((prev) => ({ ...prev, [orderId]: null }));
    }
  };

  // ── Station 6: NDR Actions ──────────────────────────────────────────────

  const handleSendNdrWhatsApp = async (orderId) => {
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/ndr/whatsapp-prompt', {
        method: 'POST',
        headers,
        body: JSON.stringify({ orderId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send WhatsApp prompt.');
      setSuccessMsg(data.message || `WhatsApp delivery rescue prompt dispatched for Order #${data.orderNumber}!`);
      fetchNdrQueue();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleScheduleReattemptSubmit = async () => {
    if (!reattemptModalOrder) return;
    setIsSubmittingReattempt(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/ndr/schedule-reattempt', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          orderId: reattemptModalOrder.orderId,
          preferredDate: reattemptDate || null,
          remarks: reattemptRemarks || 'Customer confirmed available',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to schedule reattempt.');
      setSuccessMsg(data.message || `Carrier reattempt scheduled for ${data.reattemptDate} on Order #${data.orderNumber}!`);
      setReattemptModalOrder(null);
      fetchNdrQueue();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmittingReattempt(false);
    }
  };

  // ── Station 7: Returns & Exchanges Actions ──────────────────────────────

  const handleOpenReturnQcModal = (returnOrd) => {
    setSelectedReturnOrder(returnOrd);
    const firstItem = returnOrd.items?.[0];
    setReturnQcSku(firstItem?.sku || '');
    setReturnQcMeasuredLength(firstItem?.quantity ? String(firstItem.quantity) : '1');
    setReturnQcDamagedMeters('');
    setReturnQcShelfLocation('Shelf A-1 (Restocked Returns)');
    setReturnQcGrade('GRADE_A');
    setReturnQcNotes('');
  };

  const handleProcessReturnQcSubmit = async () => {
    if (!selectedReturnOrder || !returnQcSku) return;
    setIsSubmittingReturnQc(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/returns/process-qc', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          orderId: selectedReturnOrder.orderId,
          sku: returnQcSku,
          qcGrade: returnQcGrade,
          measuredLength: Number(returnQcMeasuredLength || 0),
          damagedMeters: Number(returnQcDamagedMeters || 0),
          shelfLocation: returnQcShelfLocation,
          notes: returnQcNotes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to process return QC.');
      setSuccessMsg(data.message || 'Return QC processed successfully.');
      setSelectedReturnOrder(null);
      fetchReturnsQueue();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmittingReturnQc(false);
    }
  };

  const handleOpenExchangeModal = (ord) => {
    setExchangeModalOrder(ord);
    setExchangeSku(ord.items?.[0]?.sku || '');
    setExchangeReason('Customer Size / Color Exchange');
  };

  const handleCreateExchangeSubmit = async () => {
    if (!exchangeModalOrder || !exchangeSku) return;
    setIsSubmittingExchange(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/returns/create-exchange', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          originalOrderId: exchangeModalOrder.orderId,
          exchangeSku: exchangeSku.trim().toUpperCase(),
          replacementReason: exchangeReason,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to spawn exchange order.');
      setSuccessMsg(data.message || 'Exchange order created!');
      setExchangeModalOrder(null);
      fetchReturnsQueue();
      fetchPickList();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmittingExchange(false);
    }
  };

  const handleTriggerOutboxWorker = async () => {
    setIsProcessingOutbox(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/v1/fulfillment/outbox/process-invoices', {
        method: 'POST',
        headers,
        body: JSON.stringify({ limit: 10 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to run outbox tick.');
      setSuccessMsg(data.message || `Processed ${data.processedCount || 0} background outbox invoice jobs!`);
      fetchOutboxJobs();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessingOutbox(false);
    }
  };

  // Pick list tab counts
  const readyToPickCount = (pickData.pickList || []).filter((it) => (it.pickableCount || 0) > 0).length;
  const deficitSkusCount = (pickData.pickList || []).filter((it) => (it.deficitCount || 0) > 0 || (it.inStockCount || 0) === 0).length;
  const totalSkusCount = (pickData.pickList || []).length;

  // Filter pick list items
  const filteredPickList = (pickData.pickList || []).filter((it) => {
    // 1. Tab filter
    if (pickFilterTab === 'ready' && !(it.pickableCount > 0)) return false;
    if (pickFilterTab === 'deficit' && !(it.deficitCount > 0 || it.inStockCount === 0)) return false;

    // 2. Search filter
    if (!pickSearch) return true;
    const q = pickSearch.toLowerCase();
    return (
      it.sku?.toLowerCase().includes(q) ||
      it.title?.toLowerCase().includes(q) ||
      it.shelfLocation?.toLowerCase().includes(q)
    );
  });

  const selectedBatch = batches.find((b) => b.id === selectedBatchId);

  return (
    <div className="fulfillment-hub-container">
      {/* Top Header */}
      <div className="fulfillment-hub-header">
        <div className="header-titles">
          <h1>📦 Zero-Error Fulfillment Hub</h1>
          <p className="subtitle">
            Omnichannel Pipeline: Warehouse Picking, Production Batching, Weight Gate, Auto Invoicing, NDR & Auto Credit Notes.
          </p>
        </div>
        <div className="header-actions">
          {isAdmin ? (
            <div className="mode-toggle-group">
              <span className="mode-toggle-label">Mode:</span>
              <button
                type="button"
                className={`mode-toggle-pill ${isTestMode ? 'test-mode' : 'live-mode'}`}
                onClick={() => {
                  const next = !isTestMode;
                  setIsTestMode(next);
                  localStorage.setItem('fvs_fulfillment_test_mode', String(next));
                }}
                title={isTestMode ? 'Click to switch to Live Production Mode' : 'Click to switch to Safe Test Mode'}
              >
                {isTestMode ? '🧪 Test Mode (Active)' : '🟢 Live Production Mode'}
              </button>
            </div>
          ) : isTestMode ? (
            <div className="mode-badge-readonly test-mode">
              <span>🧪 Test Mode (Simulated)</span>
            </div>
          ) : null}

          <button
            className="refresh-btn"
            onClick={() => {
              if (activeTab === 'picking') fetchPickList();
              else if (activeTab === 'production') fetchDeficitItems();
              else if (activeTab === 'packing') fetchPackingQueue();
              else if (activeTab === 'ndr') fetchNdrQueue();
              else if (activeTab === 'returns') fetchReturnsQueue();
              else fetchOutboxJobs();
            }}
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Test Mode Banner */}
      {isTestMode && (
        <div className="test-mode-notice-banner">
          <span className="test-mode-icon">🧪</span>
          <div className="test-mode-text">
            <strong>TEST MODE ACTIVE (Dry-Run Simulation):</strong> All button actions, stock allocations, work orders, shipping labels, and returns run with simulated validations. <em>No real inventory deducted, no real orders modified, no real Zoho invoices or courier AWBs generated.</em>
          </div>
        </div>
      )}

      {/* Global Alerts */}
      {error && (
        <div className="alert-banner error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      {successMsg && (
        <div className="alert-banner success-banner">
          <span>✅ {successMsg}</span>
          <button onClick={() => setSuccessMsg('')}>×</button>
        </div>
      )}

      {/* ── 2-COLUMN FULFILLMENT PIPELINE LAYOUT (LEFT SIDEBAR + RIGHT STATION MAIN) ── */}
      <div className="fulfillment-body-layout">
        {/* Left Sidebar Navigation */}
        <aside className="fulfillment-sidebar-nav">
          <div className="sidebar-nav-header">
            <span className="sidebar-nav-label">Pipeline Stations</span>
          </div>

          <button
            type="button"
            className={`station-sidebar-btn ${activeTab === 'picking' ? 'active' : ''}`}
            onClick={() => setActiveTab('picking')}
          >
            <div className="sidebar-btn-left">
              <span className="station-icon">📦</span>
              <div className="station-info">
                <span className="station-name">1. Pick & Allocate</span>
                <span className="station-sub">{pickData.totalPiecesToPick || 0}m In Stock</span>
              </div>
            </div>
            {readyToPickCount > 0 && (
              <span className="station-count-badge green-badge">{readyToPickCount}</span>
            )}
          </button>

          <button
            type="button"
            className={`station-sidebar-btn ${activeTab === 'production' ? 'active' : ''}`}
            onClick={() => setActiveTab('production')}
          >
            <div className="sidebar-btn-left">
              <span className="station-icon">🏭</span>
              <div className="station-info">
                <span className="station-name">2. Factory Production</span>
                <span className="station-sub">{deficitData.totalDeficitPieces || 0}m Deficit</span>
              </div>
            </div>
            {deficitSkusCount > 0 && (
              <span className="station-count-badge orange-badge">{deficitSkusCount}</span>
            )}
          </button>

          <button
            type="button"
            className={`station-sidebar-btn ${activeTab === 'packing' ? 'active' : ''}`}
            onClick={() => setActiveTab('packing')}
          >
            <div className="sidebar-btn-left">
              <span className="station-icon">🚚</span>
              <div className="station-info">
                <span className="station-name">3. Pack & Ship</span>
                <span className="station-sub">{packingQueue.totalReadyToPack || 0} Ready to Pack</span>
              </div>
            </div>
            {packingQueue.totalReadyToPack > 0 && (
              <span className="station-count-badge blue-badge">{packingQueue.totalReadyToPack}</span>
            )}
          </button>

          <button
            type="button"
            className={`station-sidebar-btn ${activeTab === 'ndr' ? 'active' : ''}`}
            onClick={() => setActiveTab('ndr')}
          >
            <div className="sidebar-btn-left">
              <span className="station-icon">🚨</span>
              <div className="station-info">
                <span className="station-name">4. Delivery Issues (NDR)</span>
                <span className="station-sub">{ndrQueue.totalNdrCount || 0} Exceptions</span>
              </div>
            </div>
            {ndrQueue.totalNdrCount > 0 && (
              <span className="station-count-badge red-badge">{ndrQueue.totalNdrCount}</span>
            )}
          </button>

          <button
            type="button"
            className={`station-sidebar-btn ${activeTab === 'returns' ? 'active' : ''}`}
            onClick={() => setActiveTab('returns')}
          >
            <div className="sidebar-btn-left">
              <span className="station-icon">🔄</span>
              <div className="station-info">
                <span className="station-name">5. Returns & Exchanges</span>
                <span className="station-sub">{returnsQueue.totalInboundReturns || 0} Inbound</span>
              </div>
            </div>
            {returnsQueue.totalInboundReturns > 0 && (
              <span className="station-count-badge purple-badge">{returnsQueue.totalInboundReturns}</span>
            )}
          </button>

          <button
            type="button"
            className={`station-sidebar-btn ${activeTab === 'outbox' ? 'active' : ''}`}
            onClick={() => setActiveTab('outbox')}
          >
            <div className="sidebar-btn-left">
              <span className="station-icon">⚡</span>
              <div className="station-info">
                <span className="station-name">6. Invoice Sync</span>
                <span className="station-sub">Background Outbox</span>
              </div>
            </div>
          </button>

          <button
            type="button"
            className={`station-sidebar-btn ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <div className="sidebar-btn-left">
              <span className="station-icon">🛡️</span>
              <div className="station-info">
                <span className="station-name">7. Health Check</span>
                <span className="station-sub">5-Point Verification</span>
              </div>
            </div>
          </button>
        </aside>

        {/* Right Station Main Panel */}
        <main className="fulfillment-station-main">

      {/* ── SECTION 1: PICKING & ORDER ALLOCATION SCREEN ──────────────────────── */}
      {activeTab === 'picking' && (
        <div className="station-content-wrapper">
          <div className="station-kpi-grid">
            <div className="kpi-card green-card">
              <span className="kpi-label">In-Stock To Pick</span>
              <span className="kpi-value">{pickData.totalPiecesToPick || 0}m</span>
              <span className="kpi-sub">Fulfillable From Shelf Pieces</span>
            </div>
            <div className="kpi-card blue-card">
              <span className="kpi-label">Open Customer Demand</span>
              <span className="kpi-value">
                {pickData.pickList?.reduce((s, it) => s + (it.orders?.length || 0), 0) || 0} Orders
              </span>
              <span className="kpi-sub">Across {pickData.totalSkus || 0} Open SKUs</span>
            </div>
            <div className="kpi-card orange-card">
              <span className="kpi-label">Out of Stock (Deficit)</span>
              <span className="kpi-value">{pickData.totalDeficitPieces || 0}m</span>
              <span className="kpi-sub">Requires Factory Batching</span>
            </div>
          </div>

          {/* Quick Filter Tabs for Pick List */}
          <div className="pick-filter-subnav">
            <button
              className={`filter-pill-btn ${pickFilterTab === 'ready' ? 'active' : ''}`}
              onClick={() => setPickFilterTab('ready')}
            >
              🟢 In Stock on Shelves ({readyToPickCount})
            </button>
            <button
              className={`filter-pill-btn ${pickFilterTab === 'deficit' ? 'active' : ''}`}
              onClick={() => setPickFilterTab('deficit')}
            >
              🔴 Out of Stock / Need Factory ({deficitSkusCount})
            </button>
            <button
              className={`filter-pill-btn ${pickFilterTab === 'all' ? 'active' : ''}`}
              onClick={() => setPickFilterTab('all')}
            >
              ⚪ All Open Items ({totalSkusCount})
            </button>
          </div>

          {/* Warehouse Pick List (Full Width) */}
          <div className="pick-list-panel panel-box" style={{ width: '100%' }}>
            <div className="panel-header flex-between" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3>Warehouse Pick List ({filteredPickList.length} SKUs)</h3>
              </div>
              <div className="header-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="search-filter-box" style={{ minWidth: '240px' }}>
                  <input
                    type="text"
                    placeholder="Search SKU, location, or name..."
                    value={pickSearch}
                    onChange={(e) => setPickSearch(e.target.value)}
                  />
                </div>
                <div className="pick-view-toggle">
                  <button
                    type="button"
                    className={`pick-view-toggle-btn ${pickViewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setPickViewMode('grid')}
                    title="Card View with Large Fabric Images"
                  >
                    🔲 Card View
                  </button>
                  <button
                    type="button"
                    className={`pick-view-toggle-btn ${pickViewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setPickViewMode('table')}
                    title="Compact Table View"
                  >
                    ☰ Table View
                  </button>
                </div>
              </div>
            </div>

            {isPickLoading ? (
              <div className="loading-state">Loading pick runs...</div>
            ) : filteredPickList.length === 0 ? (
              <div className="empty-state">
                {pickFilterTab === 'ready'
                  ? 'No in-stock pieces waiting to be picked on shelves. Check Out of Stock / Factory tab.'
                  : 'No items matching the selected filter.'}
              </div>
            ) : pickViewMode === 'grid' ? (
              <div className="pick-cards-grid">
                {filteredPickList.map((item) => {
                  const isPicked = !!pickedItems[item.sku];
                  return (
                    <div key={item.sku} className={`sku-pick-card ${isPicked ? 'picked-card' : ''}`}>
                      {/* Top Bar */}
                      <div className="sku-card-top-bar">
                        <label className="card-pick-checkbox-label">
                          <input
                            type="checkbox"
                            className="pick-checkbox"
                            checked={isPicked}
                            onChange={() => handleTogglePicked(item.sku)}
                            title="Mark picked from shelf"
                          />
                          <span className="card-pick-checkbox-text">{isPicked ? '✓ Picked' : 'Pick'}</span>
                        </label>
                        <div className="card-pick-badge-wrapper">
                          <span className={`pick-badge ${item.pickableCount > 0 ? 'badge-ready' : 'badge-empty'}`}>
                            {item.pickableCount > 0 ? `⚡ ${item.pickableCount}m to pick` : '🏭 0m (Deficit)'}
                          </span>
                        </div>
                      </div>

                      {/* Large Fabric Image */}
                      <div
                        className="sku-card-img-container"
                        onClick={() => item.imageUrl && setZoomImage({ url: item.imageUrl, sku: item.sku, title: item.sku })}
                        title={item.imageUrl ? "🔍 Click to zoom fabric design" : "No image"}
                      >
                        {item.imageUrl ? (
                          <>
                            <img
                              src={item.imageUrl}
                              alt={item.sku}
                              className="sku-card-img"
                              loading="lazy"
                            />
                            <div className="card-img-zoom-hint">🔍 Zoom</div>
                          </>
                        ) : (
                          <div className="fabric-thumb-placeholder sku-card-placeholder">🧵</div>
                        )}
                      </div>

                      {/* Card Details */}
                      <div className="sku-card-content">
                        <div className="sku-card-header-line">
                          <strong className="sku-card-code" title={item.sku}>{item.sku}</strong>
                        </div>

                        <div className="sku-card-location">
                          📍 {item.shelfLocation}
                        </div>

                        {item.activePickRolls && item.activePickRolls.length > 0 && (
                          <div className="sku-card-piece-breakdown">
                            ⚡ {item.activePickRolls.length} piece{item.activePickRolls.length > 1 ? 's' : ''} ({item.activePickRolls.map((r) => `${r.originalLength}m`).join(' + ')})
                          </div>
                        )}

                        <div className="sku-card-stock-info">
                          <div className="stock-info-row">
                            <span className="stock-info-label">In Stock:</span>
                            <span className="stock-info-val">{item.inStockCount}m</span>
                          </div>
                          <div className="stock-info-divider">•</div>
                          <div className="stock-info-row">
                            <span className="stock-info-label">Needed:</span>
                            <span className="stock-info-val">{item.totalNeeded}m</span>
                          </div>
                        </div>

                        {item.deficitCount > 0 && (
                          <div className="sku-card-deficit-alert">
                            ⚠️ Deficit: -{item.deficitCount}m (Need factory)
                          </div>
                        )}
                      </div>

                      {/* Action Button */}
                      <div className="sku-card-footer">
                        {item.pickableCount > 0 ? (
                          <button
                            type="button"
                            className="action-button primary-btn card-allocate-btn"
                            onClick={() => handleOpenSkuAllocation(item)}
                            title="Open Allocation Studio"
                          >
                            ⚡ Allocate ({item.orders?.length || 0}) ➔
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="action-button secondary-btn card-deficit-btn"
                            onClick={() => {
                              setActiveTab('production');
                              setProdSubTab('deficit');
                            }}
                            title="No piece >= required length on shelf. Click to build factory batch."
                          >
                            🏭 Deficit ({item.orders?.length || 0}) ➔
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="table-container">
                <table className="station-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>Pick</th>
                      <th style={{ width: '60px' }}>Design</th>
                      <th>SKU</th>
                      <th>Location</th>
                      <th>To Pick</th>
                      <th>Stock / Needed</th>
                      <th style={{ textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPickList.map((item) => {
                      const isPicked = !!pickedItems[item.sku];
                      return (
                        <tr key={item.sku} className={isPicked ? 'picked-row' : ''}>
                          <td className="center-cell">
                            <input
                              type="checkbox"
                              className="pick-checkbox"
                              checked={isPicked}
                              onChange={() => handleTogglePicked(item.sku)}
                              title="Mark picked from shelf"
                            />
                          </td>
                          <td className="center-cell design-col">
                            {item.imageUrl ? (
                              <img
                                src={item.imageUrl}
                                alt={item.sku}
                                className="fabric-thumb-img"
                                onClick={() => setZoomImage({ url: item.imageUrl, sku: item.sku, title: item.sku })}
                                title="Click to inspect design"
                                loading="lazy"
                                style={{ width: '56px', height: '56px', borderRadius: '8px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #e2e8f0' }}
                              />
                            ) : (
                              <div className="fabric-thumb-placeholder" style={{ width: '56px', height: '56px', fontSize: '1.4rem' }}>🧵</div>
                            )}
                          </td>
                          <td>
                            <strong className="sku-code" style={{ fontSize: '0.95rem' }}>{item.sku}</strong>
                          </td>
                          <td>
                            <span className="location-pill" style={{ fontSize: '0.84rem' }}>📍 {item.shelfLocation}</span>
                          </td>
                          <td>
                            <span className={`pick-badge ${item.pickableCount > 0 ? 'badge-ready' : 'badge-empty'}`} style={{ fontSize: '0.9rem', padding: '4px 10px' }}>
                              {item.pickableCount}m
                            </span>
                            {item.activePickRolls && item.activePickRolls.length > 0 && (
                              <div style={{ fontSize: '0.74rem', color: '#047857', marginTop: '3px', fontWeight: '600' }}>
                                {item.activePickRolls.length} piece{item.activePickRolls.length > 1 ? 's' : ''} ({item.activePickRolls.map((r) => `${r.originalLength}m`).join(' + ')})
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="stock-ratio" style={{ fontWeight: '600' }}>
                              {item.inStockCount}m / {item.totalNeeded}m
                            </span>
                            {item.deficitCount > 0 && (
                              <div className="deficit-tag" style={{ display: 'inline-block', marginLeft: '6px' }}>
                                (-{item.deficitCount}m deficit)
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {item.pickableCount > 0 ? (
                              <button
                                className="action-button primary-btn mini-btn"
                                onClick={() => handleOpenSkuAllocation(item)}
                                title="Open Allocation Studio"
                                style={{ padding: '8px 16px', fontWeight: '700', fontSize: '0.85rem' }}
                              >
                                ⚡ Allocate ({item.orders?.length || 0}) ➔
                              </button>
                            ) : (
                              <button
                                className="action-button secondary-btn mini-btn"
                                onClick={() => {
                                  setActiveTab('production');
                                  setProdSubTab('deficit');
                                }}
                                title="No piece >= required length on shelf. Click to build factory batch."
                                style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', padding: '8px 16px', fontSize: '0.85rem' }}
                              >
                                🏭 Deficit ({item.orders?.length || 0}) ➔
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STATION 3: PRODUCTION BATCHES & INBOUND QC SCREEN ────────────────── */}
      {activeTab === 'production' && (
        <div className="station-content-wrapper">
          <div className="station-subnav">
            <button
              className={`subnav-btn ${prodSubTab === 'deficit' ? 'active' : ''}`}
              onClick={() => setProdSubTab('deficit')}
            >
              1. Smart Batch Builder ({deficitData.items?.length || 0} Deficit SKUs)
            </button>
            <button
              className={`subnav-btn ${prodSubTab === 'inbound_qc' ? 'active' : ''}`}
              onClick={() => setProdSubTab('inbound_qc')}
            >
              2. Inbound 1-by-1 QC Receipt & Auto-Rerouter
            </button>
            <button
              className={`subnav-btn ${prodSubTab === 'history' ? 'active' : ''}`}
              onClick={() => setProdSubTab('history')}
            >
              3. Batch Work Order History ({batches.length})
            </button>
          </div>

          {prodSubTab === 'deficit' && (
            <div className="panel-box">
              <div className="panel-header flex-between">
                <div>
                  <h3>🏭 Smart Production Batch Builder</h3>
                  <p className="panel-desc">
                    Aggregates all out-of-stock items across open orders. Select items to compile a 2–3x weekly factory batch.
                  </p>
                </div>
                <button
                  className="action-submit-btn compile-batch-btn"
                  onClick={() => setIsBatchModalOpen(true)}
                  disabled={!canCreateBatch || deficitData.items?.length === 0}
                >
                  ➕ Compile Factory Batch
                </button>
              </div>

              {isDeficitLoading ? (
                <div className="loading-state">Analyzing open order deficits...</div>
              ) : (deficitData.items || []).length === 0 ? (
                <div className="empty-state">🎉 No stock deficits! All open orders are covered by warehouse inventory.</div>
              ) : (
                <div className="table-container">
                  <table className="station-table">
                    <thead>
                      <tr>
                        <th className="center-cell">Include</th>
                        <th>Design</th>
                        <th>SKU & Product</th>
                        <th>In Stock (Meters)</th>
                        <th>Customer Demand (Meters)</th>
                        <th>In Existing Batches</th>
                        <th>Net Deficit to Produce</th>
                        <th>Waiting Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(deficitData.items || []).map((it) => (
                        <tr key={it.sku}>
                          <td className="center-cell">
                            <input
                              type="checkbox"
                              checked={!!selectedDeficitSkus[it.sku]}
                              onChange={(e) =>
                                setSelectedDeficitSkus((prev) => ({
                                  ...prev,
                                  [it.sku]: e.target.checked,
                                }))
                              }
                            />
                          </td>
                          <td className="center-cell design-col">
                            {it.imageUrl ? (
                              <img
                                src={it.imageUrl}
                                alt={it.sku}
                                className="fabric-thumb-img"
                                onClick={() => setZoomImage({ url: it.imageUrl, sku: it.sku, title: it.title })}
                                title="🔍 Click to inspect fabric design"
                                loading="lazy"
                              />
                            ) : (
                              <div className="fabric-thumb-placeholder" title="No design image uploaded">🧵</div>
                            )}
                          </td>
                          <td>
                            <strong className="sku-code">{it.sku}</strong>
                            <div className="item-title-sub">{it.title}</div>
                          </td>
                          <td>
                            <span className="stock-pill">{it.inStockMeters || it.inStockCount || 0}m ({it.inStockPieces || 0} pcs)</span>
                          </td>
                          <td>
                            <strong>{it.totalNeeded}m</strong>
                          </td>
                          <td>
                            <span>{it.alreadyInBatch || 0}m</span>
                          </td>
                          <td>
                            <span className="deficit-badge">+{it.netDeficit}m</span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="orders-count-badge-btn"
                              onClick={() => setSelectedDeficitWaitingOrders(it)}
                              title="Click to view customer orders for this deficit SKU"
                            >
                              {it.orders?.length || 0} Orders 👁️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {prodSubTab === 'inbound_qc' && (
            <div className="panel-box">
              <div className="panel-header">
                <h3>🔍 Inbound "One-by-One" QC Receipt & Zero-Orphan Auto-Rerouter</h3>
                <p className="panel-desc">
                  Measure and inspect arriving factory fabric pieces. Grade A pieces are automatically allocated to waiting customer orders and surplus is placed on shelf.
                </p>
              </div>

              <form className="inbound-qc-form" onSubmit={handleReceivePieceSubmit}>
                <div className="form-row">
                  <div className="form-group flex-1">
                    <label>Select Active Production Batch</label>
                    <select
                      value={selectedBatchId}
                      onChange={(e) => {
                        const bId = e.target.value;
                        setSelectedBatchId(bId);
                        setQcSku('');
                        const foundB = batches.find((b) => b.id === bId);
                        if (foundB?.items?.[0]) {
                          setQcSku(foundB.items[0].sku);
                          setQcMeasuredLength(foundB.items[0].quantity || 50);
                        }
                      }}
                      required
                    >
                      <option value="">-- Choose Batch --</option>
                      {batches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.batchNumber} ({b.status}) - {b.receivedPieces || 0}/{b.totalPieces || 0} pcs received
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group flex-1">
                    <label>SKU Code</label>
                    {selectedBatch?.items ? (
                      <select
                        value={qcSku}
                        onChange={(e) => {
                          const s = e.target.value;
                          setQcSku(s);
                          const itMatch = selectedBatch.items.find((it) => it.sku === s);
                          if (itMatch) setQcMeasuredLength(itMatch.quantity || 50);
                        }}
                        required
                      >
                        <option value="">-- Choose SKU from Batch --</option>
                        {selectedBatch.items.map((it) => (
                          <option key={it.sku} value={it.sku}>
                            {it.sku} ({it.receivedMeters || it.receivedCount || 0}/{it.quantity}m received)
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder="Enter SKU"
                        value={qcSku}
                        onChange={(e) => setQcSku(e.target.value)}
                        required
                      />
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group flex-1">
                    <label>📏 Measured Total Piece (Meters)</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="e.g. 50.0"
                      value={qcMeasuredLength}
                      onChange={(e) => setQcMeasuredLength(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group flex-1">
                    <label>✂️ Defect / Damaged Cut (Meters - Optional)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="e.g. 2.0 (Quarantines defect)"
                      value={qcDamagedMeters}
                      onChange={(e) => setQcDamagedMeters(e.target.value)}
                    />
                  </div>

                  <div className="form-group flex-1">
                    <label>📍 Target Shelf / Bin Location</label>
                    <input
                      type="text"
                      placeholder="e.g. Shelf B-2 (Finished Goods)"
                      value={qcShelfLocation}
                      onChange={(e) => setQcShelfLocation(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group flex-1">
                    <label>Piece Barcode / Sticker ID (Optional)</label>
                    <input
                      type="text"
                      placeholder="Scan piece barcode (or auto-generate)..."
                      value={qcPieceBarcode}
                      onChange={(e) => setQcPieceBarcode(e.target.value)}
                    />
                  </div>

                  <div className="form-group flex-1">
                    <label>Inbound QC Inspection Grade</label>
                    <select value={qcGrade} onChange={(e) => setQcGrade(e.target.value)}>
                      <option value="GRADE_A">⭐ Grade A: Perfect / Like New (Auto-Allocates & Restocks)</option>
                      <option value="GRADE_B">🛠️ Grade B: Minor Rework / Defect Clearance</option>
                      <option value="GRADE_C">❌ Grade C: Defect / Reject (Scrap / Dispute)</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Inspection Notes (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. print sharpness verified, colorway approved"
                    value={qcNotes}
                    onChange={(e) => setQcNotes(e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  className="action-submit-btn receive-qc-btn"
                  disabled={isReceivingPiece || !canReceiveQC}
                >
                  {isReceivingPiece ? 'Receiving & Auto-Allocating...' : '📥 Confirm QC & Receive Piece (1-by-1)'}
                </button>
              </form>

              {routingFeedback && (
                <div
                  className={`routing-result-banner ${
                    routingFeedback.routing?.status === 'ALLOCATED_TO_ORDER'
                      ? 'route-allocated'
                      : routingFeedback.routing?.status === 'REALLOCATED_TO_WAITING_ORDER'
                      ? 'route-reassigned'
                      : 'route-restocked'
                  }`}
                >
                  <div className="route-header">
                    <h4>
                      {routingFeedback.routing?.status === 'ALLOCATED_TO_ORDER' && '🟢 Piece Allocated to Active Order'}
                      {routingFeedback.routing?.status === 'REALLOCATED_TO_WAITING_ORDER' && '🟠 Zero-Orphan Auto-Reallocation Activated!'}
                      {routingFeedback.routing?.status === 'RESTOCKED_TO_SHELF' && '🔵 Auto-Restocked to Finished Inventory'}
                      {routingFeedback.routing?.status === 'SCRAP_REJECTED' && '❌ Defect Rejected for Factory Dispute'}
                    </h4>
                    <span className="route-dest-badge">Destination: {routingFeedback.routing?.destination}</span>
                  </div>
                  <p className="route-msg">{routingFeedback.routing?.message}</p>
                  <div className="route-details">
                    <span>Batch: <strong>{routingFeedback.batchNumber}</strong></span>
                    <span>Piece ID: <code>{routingFeedback.pieceId}</code></span>
                    <span>SKU: <strong>{routingFeedback.sku}</strong></span>
                    <span>Batch Progress: <strong>{routingFeedback.totalReceived} / {routingFeedback.totalPieces} pcs</strong></span>
                  </div>
                </div>
              )}
            </div>
          )}

          {prodSubTab === 'history' && (
            <div className="panel-box">
              <div className="panel-header">
                <h3>📜 Production Work Order Batches</h3>
              </div>
              <div className="table-container">
                <table className="station-table">
                  <thead>
                    <tr>
                      <th>Batch #</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Total Pieces</th>
                      <th>Created Date</th>
                      <th>Target Delivery</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <strong className="batch-code">{b.batchNumber}</strong>
                        </td>
                        <td>
                          <span className={`status-tag status-${(b.status || '').toLowerCase()}`}>
                            {b.status}
                          </span>
                        </td>
                        <td>
                          <div className="progress-bar-container">
                            <div
                              className="progress-bar-fill"
                              style={{
                                width: `${Math.min(100, Math.round(((b.receivedPieces || 0) / (b.totalPieces || 1)) * 100))}%`,
                              }}
                            />
                          </div>
                          <span className="progress-text">
                            {b.receivedPieces || 0} / {b.totalPieces || 0} pcs ({Math.min(100, Math.round(((b.receivedPieces || 0) / (b.totalPieces || 1)) * 100))}%)
                          </span>
                        </td>
                        <td><strong>{b.totalPieces} pcs</strong></td>
                        <td>{b.createdIso ? new Date(b.createdIso).toLocaleDateString() : 'N/A'}</td>
                        <td>{b.targetDeliveryDate || 'N/A'}</td>
                        <td><span className="notes-text">{b.notes || '-'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STATION 4: PACKING DESK & COURIER HANDOVER SCREEN ────────────────── */}
      {activeTab === 'packing' && (
        <div className="station-content-wrapper">
          <div className="station-kpi-grid">
            <div className="kpi-card green-card">
              <span className="kpi-label">Ready to Pack</span>
              <span className="kpi-value">{packingQueue.totalReadyToPack}</span>
              <span className="kpi-sub">100% Items Available</span>
            </div>
            <div className="kpi-card blue-card">
              <span className="kpi-label">Packed & Labeled</span>
              <span className="kpi-value">{packingQueue.totalPackedReadyToDispatch}</span>
              <span className="kpi-sub">Ready for Courier Pickup</span>
            </div>
          </div>

          <div className="panel-box" style={{ marginBottom: '1.5rem' }}>
            <div className="panel-header">
              <h3>📦 1. Ready to Pack Queue (Weight Scale Gate)</h3>
              <p className="panel-desc">
                All items for these orders are picked and allocated. Enter box gross weight and dimensions to generate shipping label.
              </p>
            </div>

            {isPackingLoading ? (
              <div className="loading-state">Loading packing queue...</div>
            ) : packingQueue.readyOrders?.length === 0 ? (
              <div className="empty-state">No orders waiting to be packed right now.</div>
            ) : (
              <div className="table-container">
                <table className="station-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer & Items</th>
                      <th>Weight Scale Gate (Grams)</th>
                      <th>Box Dimensions (L×W×H cm)</th>
                      <th>Carrier</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packingQueue.readyOrders.map((ord) => {
                      const form = packingForms[ord.orderId] || {
                        grossWeightGrams: ord.estimatedWeightGrams || 450,
                        length: 25,
                        width: 20,
                        height: 5,
                        carrier: 'shiprocket',
                      };
                      const isLabeling = actionInProgress[ord.orderId] === 'label';

                      return (
                        <tr key={ord.orderId}>
                          <td>
                            <strong className="order-number">#{ord.orderNumber}</strong>
                            <div className="item-title-sub">{ord.itemsCount} items</div>
                          </td>
                          <td>
                            <div className="customer-name">{ord.customerName}</div>
                            <div className="item-list-preview">
                              {ord.items.map((it, idx) => (
                                <span key={idx} className="item-mini-tag">
                                  {it.imageUrl && (
                                    <img
                                      src={it.imageUrl}
                                      alt={it.sku}
                                      className="fabric-thumb-mini"
                                      onClick={() => setZoomImage({ url: it.imageUrl, title: `Order #${ord.orderNumber} - ${it.sku}` })}
                                      title="Click to Zoom Fabric"
                                    />
                                  )}
                                  <strong>{it.sku}</strong> ({it.quantity}m)
                                  {it.lockedPieceId && (
                                    <span className="piece-chip-tag" title="Allocated Physical Piece">
                                      🏷️ {it.lockedPieceId}
                                    </span>
                                  )}
                                  {it.usedPieces && it.usedPieces.length > 0 && (
                                    <span className="piece-chip-tag" title="Allocated Physical Cut Pieces">
                                      🏷️ {it.usedPieces.map(p => `${p.pieceId} (${p.cutMeters || p.length}m)`).join(', ')}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    className="mini-unallocate-btn"
                                    onClick={() => handleUnallocateStock(ord.orderId, it.sku, it.id)}
                                    title="Release piece meters back to shelf inventory"
                                  >
                                    ↩️
                                  </button>
                                </span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="weight-input"
                              placeholder="e.g. 450g"
                              value={form.grossWeightGrams}
                              onChange={(e) =>
                                handleFormChange(ord.orderId, 'grossWeightGrams', e.target.value)
                              }
                              min="1"
                              required
                            />
                            {ord.estimatedWeightGrams && (
                              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '3px' }}>
                                Est: ~{ord.estimatedWeightGrams}g (Fabric + Box)
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="dim-inputs-row">
                              <input
                                type="number"
                                placeholder="L"
                                value={form.length}
                                onChange={(e) => handleFormChange(ord.orderId, 'length', e.target.value)}
                                className="dim-input"
                              />
                              <span>×</span>
                              <input
                                type="number"
                                placeholder="W"
                                value={form.width}
                                onChange={(e) => handleFormChange(ord.orderId, 'width', e.target.value)}
                                className="dim-input"
                              />
                              <span>×</span>
                              <input
                                type="number"
                                placeholder="H"
                                value={form.height}
                                onChange={(e) => handleFormChange(ord.orderId, 'height', e.target.value)}
                                className="dim-input"
                              />
                            </div>
                          </td>
                          <td>
                            <select
                              value={form.carrier}
                              onChange={(e) => handleFormChange(ord.orderId, 'carrier', e.target.value)}
                              className="carrier-select"
                            >
                              <option value="shiprocket">Shiprocket</option>
                              <option value="delhivery">Delhivery Direct</option>
                            </select>
                          </td>
                          <td>
                            <button
                              className="action-submit-btn compile-batch-btn"
                              onClick={() => handleGenerateLabel(ord.orderId)}
                              disabled={isLabeling || !canGenerateLabel}
                            >
                              {isLabeling ? 'Generating AWB...' : '🏷️ Generate Carrier Label'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="panel-box">
            <div className="panel-header">
              <h3>🚚 2. Courier Handover & Manifest Queue (AWB Hard Lock)</h3>
              <p className="panel-desc">
                Shipping label attached. Hand over parcel to driver and click "Confirm Courier Handover" to move to SHIPPED and trigger automated Zoho Invoicing.
              </p>
            </div>

            {packingQueue.packedOrders?.length === 0 ? (
              <div className="empty-state">No packed parcels waiting for courier pickup.</div>
            ) : (
              <div className="table-container">
                <table className="station-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Carrier & Verified AWB</th>
                      <th>Gross Weight</th>
                      <th>Invoicing Status</th>
                      <th>Action (AWB Locked)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packingQueue.packedOrders.map((ord) => {
                      const hasAwb = !!ord.carrierLabel?.awb;
                      const isDispatching = actionInProgress[ord.orderId] === 'dispatch';

                      return (
                        <tr key={ord.orderId}>
                          <td>
                            <strong className="order-number">#{ord.orderNumber}</strong>
                          </td>
                          <td>
                            <div className="customer-name">{ord.customerName}</div>
                          </td>
                          <td>
                            <div>
                              <span className="carrier-badge">{ord.carrierLabel?.carrier || 'COURIER'}</span>
                              <strong className="awb-code">{ord.carrierLabel?.awb || 'No AWB'}</strong>
                            </div>
                            {ord.carrierLabel?.labelUrl && (
                              <a
                                href={ord.carrierLabel.labelUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="label-download-link"
                              >
                                🖨️ Print $4\times6$ Label
                              </a>
                            )}
                          </td>
                          <td>
                            <span>{ord.carrierLabel?.grossWeightGrams || ord.packagingDetails?.grossWeightGrams || '-'} g</span>
                          </td>
                          <td>
                            {ord.zohoInvoiceNumber ? (
                              <span className="invoice-badge invoiced">✅ {ord.zohoInvoiceNumber}</span>
                            ) : ord.invoicePending ? (
                              <span className="invoice-badge pending">⚠️ Invoice Pending in Outbox</span>
                            ) : (
                              <span className="invoice-badge queue">Queued on Dispatch</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="action-submit-btn mark-used-btn"
                              onClick={() => handleDispatchOrder(ord.orderId)}
                              disabled={!hasAwb || isDispatching || !canDispatch}
                              title={!hasAwb ? 'AWB Hard Lock: Generate label first' : 'Authorize driver handover'}
                            >
                              {isDispatching ? 'Dispatching...' : '🚀 Confirm Courier Handover'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STATION 6: NDR ACTION CENTER & LIVE TRACKING ────────────────────── */}
      {activeTab === 'ndr' && (
        <div className="station-content-wrapper">
          <div className="station-kpi-grid">
            <div className="kpi-card orange-card">
              <span className="kpi-label">Active Delivery Exceptions</span>
              <span className="kpi-value">{ndrQueue.totalNdrCount}</span>
              <span className="kpi-sub">Requires Customer Action</span>
            </div>
          </div>

          <div className="panel-box">
            <div className="panel-header">
              <h3>🚨 Live NDR Action Center</h3>
              <p className="panel-desc">
                When carriers fail delivery (e.g. customer unreachable, door closed), trigger 1-click WhatsApp customer prompts to reschedule and cut RTO rates by up to 50%.
              </p>
            </div>

            {isNdrLoading ? (
              <div className="loading-state">Checking live carrier delivery exceptions...</div>
            ) : ndrQueue.ndrOrders?.length === 0 ? (
              <div className="empty-state">🎉 Zero delivery exceptions! All dispatched parcels are in transit or delivered.</div>
            ) : (
              <div className="table-container">
                <table className="station-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer & Phone</th>
                      <th>AWB / Carrier</th>
                      <th>Exception Reason</th>
                      <th>WhatsApp Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ndrQueue.ndrOrders.map((ord) => (
                      <tr key={ord.orderId}>
                        <td>
                          <strong className="order-number">#{ord.orderNumber}</strong>
                        </td>
                        <td>
                          <div className="customer-name">{ord.customerName}</div>
                          <div className="item-title-sub">📞 {ord.phone || 'No phone'}</div>
                        </td>
                        <td>
                          <span className="carrier-badge">{ord.carrier}</span>
                          <strong className="awb-code">{ord.awb}</strong>
                        </td>
                        <td>
                          <span className="deficit-tag" style={{ marginLeft: 0 }}>
                            ⚠️ {ord.ndrReason}
                          </span>
                        </td>
                        <td>
                          {ord.whatsAppSent ? (
                            <span className="invoice-badge invoiced">✅ WhatsApp Sent</span>
                          ) : (
                            <span className="invoice-badge pending">Not Sent</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <button
                              className="action-submit-btn"
                              style={{ background: '#25D366', color: 'white', fontSize: '0.8rem', padding: '6px 10px' }}
                              onClick={() => handleSendNdrWhatsApp(ord.orderId)}
                              disabled={!canHandleNdr}
                              title="Send WhatsApp customer delivery prompt"
                            >
                              💬 1-Click WhatsApp
                            </button>
                            <button
                              className="action-submit-btn compile-batch-btn"
                              style={{ fontSize: '0.8rem', padding: '6px 10px' }}
                              onClick={() => {
                                setReattemptModalOrder(ord);
                                setReattemptDate(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
                                setReattemptRemarks('');
                              }}
                              disabled={!canHandleNdr}
                            >
                              📅 Reattempt API
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STATION 7: RETURNS, QC & AUTOMATED CREDIT NOTES ─────────────────── */}
      {activeTab === 'returns' && (
        <div className="station-content-wrapper">
          <div className="station-kpi-grid">
            <div className="kpi-card blue-card">
              <span className="kpi-label">Inbound Returns & RTOs</span>
              <span className="kpi-value">{returnsQueue.totalInboundReturns}</span>
              <span className="kpi-sub">Awaiting Inbound QC Inspection</span>
            </div>
          </div>

          <div className="panel-box">
            <div className="panel-header">
              <h3>🔄 Reverse Logistics, Return QC & Automated Zoho Credit Notes</h3>
              <p className="panel-desc">
                Inspect returning parcels. Grade A goods are instantly restocked to shelf & Shopify. Automated Zoho Credit Notes reverse invoice output GST.
              </p>
            </div>

            {isReturnsLoading ? (
              <div className="loading-state">Loading inbound returns queue...</div>
            ) : returnsQueue.returns?.length === 0 ? (
              <div className="empty-state">No returning parcels in transit or awaiting QC inspection.</div>
            ) : (
              <div className="table-container">
                <table className="station-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer & Items</th>
                      <th>Tracking AWB</th>
                      <th>Return Reason</th>
                      <th>Invoice / Credit Note</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returnsQueue.returns.map((ret) => (
                      <tr key={ret.orderId}>
                        <td>
                          <strong className="order-number">#{ret.orderNumber}</strong>
                        </td>
                        <td>
                          <div className="customer-name">{ret.customerName}</div>
                          <div className="item-list-preview">
                            {ret.items.map((it, idx) => (
                              <span key={idx} className="item-mini-tag">
                                {it.imageUrl && (
                                  <img
                                    src={it.imageUrl}
                                    alt={it.sku}
                                    className="fabric-thumb-mini"
                                    onClick={() => setZoomImage({ url: it.imageUrl, title: `Return Order #${ret.orderNumber} - ${it.sku}` })}
                                    title="Click to Zoom Fabric"
                                  />
                                )}
                                {it.sku} (x{it.quantity})
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <strong className="awb-code">{ret.awb}</strong>
                        </td>
                        <td>
                          <span className="notes-text">{ret.returnReason}</span>
                        </td>
                        <td>
                          <div>
                            {ret.zohoInvoiceNumber ? (
                              <div style={{ fontSize: '0.8rem', color: '#475569' }}>Inv: {ret.zohoInvoiceNumber}</div>
                            ) : null}
                            {ret.zohoCreditNoteNumber ? (
                              <span className="invoice-badge invoiced">CN: {ret.zohoCreditNoteNumber}</span>
                            ) : (
                              <span className="invoice-badge queue">CN Pending QC</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <button
                              className="action-submit-btn receive-qc-btn"
                              style={{ margin: 0, fontSize: '0.8rem', padding: '6px 10px' }}
                              onClick={() => handleOpenReturnQcModal(ret)}
                              disabled={!canProcessReturns}
                            >
                              🔍 Inbound Return QC
                            </button>
                            <button
                              className="action-submit-btn compile-batch-btn"
                              style={{ fontSize: '0.8rem', padding: '6px 10px' }}
                              onClick={() => handleOpenExchangeModal(ret)}
                              disabled={!canCreateExchange}
                            >
                              🔁 1-Click Exchange
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SECTION 6: INVOICE & CREDIT NOTE SYNC QUEUE ──────────────────────── */}
      {activeTab === 'outbox' && (
        <div className="station-content-wrapper">
          <div className="panel-box">
            <div className="panel-header flex-between">
              <div>
                <h3>⚡ Automated Invoice & Sync Queue</h3>
                <p className="panel-desc">
                  Monitors automated Zoho Invoicing, Credit Notes, Carrier Webhooks, and retry backoffs with zero silent drops.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="action-submit-btn compile-batch-btn"
                  onClick={handleTriggerOutboxWorker}
                  disabled={isProcessingOutbox}
                >
                  {isProcessingOutbox ? 'Running Sync...' : '⚡ Process Pending Invoices Now'}
                </button>
                <button className="refresh-btn" onClick={fetchOutboxJobs}>
                  🔄 Refresh Queue
                </button>
              </div>
            </div>

            {isOutboxLoading ? (
              <div className="loading-state">Loading outbox queue...</div>
            ) : outboxJobs.length === 0 ? (
              <div className="empty-state">No pending background sync jobs. Outbox is clear!</div>
            ) : (
              <div className="table-container">
                <table className="station-table">
                  <thead>
                    <tr>
                      <th>Job Type</th>
                      <th>Status</th>
                      <th>Attempts</th>
                      <th>Reference ID</th>
                      <th>Created</th>
                      <th>Last Error / Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outboxJobs.map((j) => (
                      <tr key={j.id}>
                        <td>
                          <code>{j.jobType}</code>
                        </td>
                        <td>
                          <span className={`outbox-status-tag status-${(j.status || '').toLowerCase()}`}>
                            {j.status}
                          </span>
                        </td>
                        <td>{j.attempts} / {j.maxAttempts}</td>
                        <td><strong>{j.referenceId || 'N/A'}</strong></td>
                        <td>{j.createdAt ? new Date(j.createdAt._seconds * 1000).toLocaleString() : 'N/A'}</td>
                        <td>
                          {j.result?.invoiceNumber ? (
                            <span className="invoice-success-text">Invoice: {j.result.invoiceNumber}</span>
                          ) : j.lastError ? (
                            <span className="error-text">{j.lastError.message}</span>
                          ) : (
                            <span className="text-muted">None</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STATION 5 / PHASE 5: PIPELINE HEALTH & NIGHTLY AUDIT SCREEN ───────── */}
      {activeTab === 'audit' && (
        <div className="station-content-wrapper">
          <div className="station-kpi-grid">
            <div className={`kpi-card ${!auditReport ? 'blue-card' : auditReport.healthScore >= 90 ? 'green-card' : auditReport.healthScore >= 70 ? 'orange-card' : 'red-card'}`}>
              <span className="kpi-label">Pipeline Health Score</span>
              <span className="kpi-value">{auditReport ? `${auditReport.healthScore}%` : '---'}</span>
              <span className="kpi-sub">{auditReport ? (auditReport.healthScore === 100 ? 'Zero-Error Status Active' : 'Anomalies Detected') : 'Run Audit to Evaluate'}</span>
            </div>
            <div className="kpi-card blue-card">
              <span className="kpi-label">Orders Scanned</span>
              <span className="kpi-value">{auditReport?.summary?.totalOrdersScanned ?? 0}</span>
              <span className="kpi-sub">Dual-Run Sample Size</span>
            </div>
            <div className="kpi-card orange-card">
              <span className="kpi-label">Dead-Letter Anomalies</span>
              <span className="kpi-value">{auditReport?.checks?.deadLetterOutboxJobs?.count ?? 0}</span>
              <span className="kpi-sub">Outbox Sync Exceptions</span>
            </div>
            <div className="kpi-card green-card">
              <span className="kpi-label">Scan Latency</span>
              <span className="kpi-value">{auditReport?.summary?.executionDurationMs ? `${auditReport.summary.executionDurationMs} ms` : '---'}</span>
              <span className="kpi-sub">5-Point Verification Speed</span>
            </div>
          </div>

          <div className="panel-box audit-control-panel">
            <div className="panel-header">
              <div>
                <h3>🛡️ 5-Point Parity Health Verification</h3>
                <p className="panel-subtitle-text">
                  Automated background health checks verifying duplicate orders, Zoho invoicing sync, dead-letter jobs, stock locks, and NDR coverage.
                </p>
              </div>
              <div className="header-actions">
                <button
                  className="action-button primary-btn"
                  onClick={handleRunAudit}
                  disabled={isRunningAudit}
                >
                  {isRunningAudit ? '⏳ Scanning Pipeline...' : '⚡ Run Parity Audit Now'}
                </button>
                <button className="refresh-btn" onClick={fetchAuditLogs}>
                  🔄 Refresh Logs
                </button>
              </div>
            </div>

            {/* 5-Point Checklist Grid */}
            <div className="audit-checklist-grid">
              {/* Check 1 */}
              <div className={`checklist-card status-${(auditReport?.checks?.duplicateSalesOrders?.status || 'passed').toLowerCase()}`}>
                <div className="checklist-header">
                  <span className="check-number">1</span>
                  <h4>Duplicate Sales Orders</h4>
                  <span className={`check-badge badge-${(auditReport?.checks?.duplicateSalesOrders?.status || 'passed').toLowerCase()}`}>
                    {auditReport?.checks?.duplicateSalesOrders?.status || 'PASSED'}
                  </span>
                </div>
                <p className="check-desc">Ensures no duplicate reference or order numbers exist across active and legacy channels.</p>
                <div className="check-metric">
                  Anomalies: <strong>{auditReport?.checks?.duplicateSalesOrders?.count || 0}</strong>
                </div>
              </div>

              {/* Check 2 */}
              <div className={`checklist-card status-${(auditReport?.checks?.uninvoicedShippedOrders?.status || 'passed').toLowerCase()}`}>
                <div className="checklist-header">
                  <span className="check-number">2</span>
                  <h4>Uninvoiced Shipped Orders</h4>
                  <span className={`check-badge badge-${(auditReport?.checks?.uninvoicedShippedOrders?.status || 'passed').toLowerCase()}`}>
                    {auditReport?.checks?.uninvoicedShippedOrders?.status || 'PASSED'}
                  </span>
                </div>
                <p className="check-desc">Detects any orders marked SHIPPED whose Zoho Sales Order has not yet generated an official Invoice.</p>
                <div className="check-metric">
                  Anomalies: <strong>{auditReport?.checks?.uninvoicedShippedOrders?.count || 0}</strong>
                </div>
              </div>

              {/* Check 3 */}
              <div className={`checklist-card status-${(auditReport?.checks?.deadLetterOutboxJobs?.status || 'passed').toLowerCase()}`}>
                <div className="checklist-header">
                  <span className="check-number">3</span>
                  <h4>Dead-Letter Outbox Jobs</h4>
                  <span className={`check-badge badge-${(auditReport?.checks?.deadLetterOutboxJobs?.status || 'passed').toLowerCase()}`}>
                    {auditReport?.checks?.deadLetterOutboxJobs?.status || 'PASSED'}
                  </span>
                </div>
                <p className="check-desc">Monitors background sync jobs that exceeded maximum retries and require operational triage.</p>
                <div className="check-metric">
                  Anomalies: <strong>{auditReport?.checks?.deadLetterOutboxJobs?.count || 0}</strong>
                </div>
              </div>

              {/* Check 4 */}
              <div className={`checklist-card status-${(auditReport?.checks?.stockReservationParity?.status || 'passed').toLowerCase()}`}>
                <div className="checklist-header">
                  <span className="check-number">4</span>
                  <h4>Stock Lock Parity</h4>
                  <span className={`check-badge badge-${(auditReport?.checks?.stockReservationParity?.status || 'passed').toLowerCase()}`}>
                    {auditReport?.checks?.stockReservationParity?.status || 'PASSED'}
                  </span>
                </div>
                <p className="check-desc">Verifies every line item marked as 'used' is backed by a physical inventory piece or shelf reservation.</p>
                <div className="check-metric">
                  Anomalies: <strong>{auditReport?.checks?.stockReservationParity?.count || 0}</strong>
                </div>
              </div>

              {/* Check 5 */}
              <div className={`checklist-card status-${(auditReport?.checks?.ndrResponseCoverage?.status || 'passed').toLowerCase()}`}>
                <div className="checklist-header">
                  <span className="check-number">5</span>
                  <h4>Active NDR Response Coverage</h4>
                  <span className={`check-badge badge-${(auditReport?.checks?.ndrResponseCoverage?.status || 'passed').toLowerCase()}`}>
                    {auditReport?.checks?.ndrResponseCoverage?.status || 'PASSED'}
                  </span>
                </div>
                <p className="check-desc">Ensures delivery exceptions have received either WhatsApp rescue outreach or carrier reattempt.</p>
                <div className="check-metric">
                  Anomalies: <strong>{auditReport?.checks?.ndrResponseCoverage?.count || 0}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Historical Audit Logs */}
          <div className="panel-box">
            <div className="panel-header">
              <h3>📜 Recent Parity Audit Run Logs</h3>
            </div>

            {isAuditLoading ? (
              <div className="loading-state">Loading audit history...</div>
            ) : auditLogs.length === 0 ? (
              <div className="empty-state">No audit logs recorded yet. Click "⚡ Run Parity Audit Now" to perform the first scan.</div>
            ) : (
              <div className="table-container">
                <table className="station-table">
                  <thead>
                    <tr>
                      <th>Audit Timestamp</th>
                      <th>Health Score</th>
                      <th>Triggered By</th>
                      <th>Orders Scanned</th>
                      <th>Duration</th>
                      <th>Status Breakdown</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td>
                          <strong>{log.auditTimestamp ? new Date(log.auditTimestamp).toLocaleString() : 'N/A'}</strong>
                        </td>
                        <td>
                          <span className={`health-score-pill ${log.healthScore >= 90 ? 'score-good' : log.healthScore >= 70 ? 'score-warning' : 'score-bad'}`}>
                            {log.healthScore}%
                          </span>
                        </td>
                        <td><code>{log.triggeredBy || 'system'}</code></td>
                        <td>{log.summary?.totalOrdersScanned || 0}</td>
                        <td>{log.summary?.executionDurationMs ? `${log.summary.executionDurationMs} ms` : 'N/A'}</td>
                        <td>
                          <div className="mini-check-badges">
                            <span title="Duplicates" className={`mini-badge ${(log.checks?.duplicateSalesOrders?.status || 'passed').toLowerCase()}`}>SO</span>
                            <span title="Invoicing" className={`mini-badge ${(log.checks?.uninvoicedShippedOrders?.status || 'passed').toLowerCase()}`}>INV</span>
                            <span title="Dead Letters" className={`mini-badge ${(log.checks?.deadLetterOutboxJobs?.status || 'passed').toLowerCase()}`}>DLQ</span>
                            <span title="Stock Parity" className={`mini-badge ${(log.checks?.stockReservationParity?.status || 'passed').toLowerCase()}`}>STK</span>
                            <span title="NDR Coverage" className={`mini-badge ${(log.checks?.ndrResponseCoverage?.status || 'passed').toLowerCase()}`}>NDR</span>
                          </div>
                        </td>
                        <td>
                          <button
                            className="action-button secondary-btn mini-btn"
                            onClick={() => {
                              setSelectedAuditLog(log);
                              setAuditReport(log);
                            }}
                          >
                            🔍 View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
        </main>
      </div>

      {/* Compile Batch Modal */}
      {isBatchModalOpen && (
        <Modal
          isOpen={isBatchModalOpen}
          onClose={() => setIsBatchModalOpen(false)}
          contentClassName="batch-create-modal"
        >
          <h2>🏭 Compile Production Work Order</h2>
          <p className="modal-desc">
            You are compiling a factory production batch for all selected deficit items across open customer orders.
          </p>

          <div className="modal-form">
            <div className="form-group">
              <label>Target Delivery Date</label>
              <input
                type="date"
                value={batchDeliveryDate}
                onChange={(e) => setBatchDeliveryDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Factory Production Notes / Work Order Instructions</label>
              <textarea
                rows={3}
                placeholder="e.g. Standard 60 GSM pure cotton fabric, double stitch hem..."
                value={batchNotes}
                onChange={(e) => setBatchNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button
              className="action-button cancel-btn"
              onClick={() => setIsBatchModalOpen(false)}
              disabled={isCreatingBatch}
            >
              Cancel
            </button>
            <button
              className="action-button save-btn"
              onClick={handleCreateBatchSubmit}
              disabled={isCreatingBatch}
            >
              {isCreatingBatch ? 'Compiling Batch Work Order...' : '🚀 Compile & Dispatch Batch'}
            </button>
          </div>
        </Modal>
      )}

      {/* Schedule Carrier Reattempt Modal */}
      {reattemptModalOrder && (
        <Modal
          isOpen={!!reattemptModalOrder}
          onClose={() => setReattemptModalOrder(null)}
          contentClassName="batch-create-modal"
        >
          <h2>📅 Schedule Carrier Reattempt: Order #{reattemptModalOrder.orderNumber}</h2>
          <p className="modal-desc">
            Call the carrier API to reschedule parcel delivery for <strong>{reattemptModalOrder.customerName}</strong> (AWB: {reattemptModalOrder.awb}).
          </p>

          <div className="modal-form">
            <div className="form-group">
              <label>Preferred Delivery Date</label>
              <input
                type="date"
                value={reattemptDate}
                onChange={(e) => setReattemptDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Driver Instructions / Customer Confirmation Remarks</label>
              <input
                type="text"
                placeholder="e.g. Customer confirmed available afternoon"
                value={reattemptRemarks}
                onChange={(e) => setReattemptRemarks(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button
              className="action-button cancel-btn"
              onClick={() => setReattemptModalOrder(null)}
              disabled={isSubmittingReattempt}
            >
              Cancel
            </button>
            <button
              className="action-button save-btn"
              onClick={handleScheduleReattemptSubmit}
              disabled={isSubmittingReattempt}
            >
              {isSubmittingReattempt ? 'Scheduling...' : '🚀 Submit Carrier Reattempt API'}
            </button>
          </div>
        </Modal>
      )}

      {/* Return QC Grading Modal */}
      {selectedReturnOrder && (
        <Modal
          isOpen={!!selectedReturnOrder}
          onClose={() => setSelectedReturnOrder(null)}
          contentClassName="batch-create-modal"
        >
          <h2>🔍 Return QC Inspection: Order #{selectedReturnOrder.orderNumber}</h2>
          <p className="modal-desc">
            Grade the arriving return piece. Grade A goods are automatically restocked to shelf & Shopify.
          </p>

          <div className="modal-form">
            <div className="form-group">
              <label>SKU to Inspect</label>
              <select
                value={returnQcSku}
                onChange={(e) => setReturnQcSku(e.target.value)}
                required
              >
                {(selectedReturnOrder.items || []).map((it) => (
                  <option key={it.sku} value={it.sku}>
                    {it.sku} ({it.title || ''})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>QC Inspection Grading</label>
              <select
                value={returnQcGrade}
                onChange={(e) => setReturnQcGrade(e.target.value)}
              >
                <option value="GRADE_A">⭐ Grade A: Like New / Resellable (Auto-Restock to Shelf & Shopify)</option>
                <option value="GRADE_B">🛠️ Grade B: Minor Rework (Route to Factory Refurbishment)</option>
                <option value="GRADE_C">❌ Grade C: Scrap / Dispute Claim (Carrier / Return Claim)</option>
              </select>
            </div>

            {returnQcGrade === 'GRADE_A' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>📏 Measured Returned Length (Meters)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    placeholder="e.g. 5.0"
                    value={returnQcMeasuredLength}
                    onChange={(e) => setReturnQcMeasuredLength(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>✂️ Defect Cut (Meters - Optional)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="e.g. 1.0 (Quarantines defect)"
                    value={returnQcDamagedMeters}
                    onChange={(e) => setReturnQcDamagedMeters(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>📍 Target Shelf / Bin Location</label>
                  <input
                    type="text"
                    placeholder="e.g. Shelf A-1 (Restocked Returns)"
                    value={returnQcShelfLocation}
                    onChange={(e) => setReturnQcShelfLocation(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>QC Inspection Notes</label>
              <input
                type="text"
                placeholder="e.g. Unopened parcel, pristine condition"
                value={returnQcNotes}
                onChange={(e) => setReturnQcNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button
              className="action-button cancel-btn"
              onClick={() => setSelectedReturnOrder(null)}
              disabled={isSubmittingReturnQc}
            >
              Cancel
            </button>
            <button
              className="action-button save-btn"
              onClick={handleProcessReturnQcSubmit}
              disabled={isSubmittingReturnQc}
            >
              {isSubmittingReturnQc ? 'Processing Return QC...' : '✅ Complete QC & Trigger Credit Note'}
            </button>
          </div>
        </Modal>
      )}

      {/* 1-Click Exchange Modal */}
      {exchangeModalOrder && (
        <Modal
          isOpen={!!exchangeModalOrder}
          onClose={() => setExchangeModalOrder(null)}
          contentClassName="batch-create-modal"
        >
          <h2>🔁 1-Click Replacement Order: #{exchangeModalOrder.orderNumber}</h2>
          <p className="modal-desc">
            Spawn a linked replacement order (e.g. <strong>#{exchangeModalOrder.orderNumber}-EX1</strong>) for <strong>{exchangeModalOrder.customerName}</strong>.
          </p>

          <div className="modal-form">
            <div className="form-group">
              <label>Replacement Item SKU</label>
              <input
                type="text"
                placeholder="Enter replacement SKU (e.g. FVS-DRS-001-BLU-L)"
                value={exchangeSku}
                onChange={(e) => setExchangeSku(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Exchange Reason</label>
              <input
                type="text"
                value={exchangeReason}
                onChange={(e) => setExchangeReason(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button
              className="action-button cancel-btn"
              onClick={() => setExchangeModalOrder(null)}
              disabled={isSubmittingExchange}
            >
              Cancel
            </button>
            <button
              className="action-button save-btn"
              onClick={handleCreateExchangeSubmit}
              disabled={isSubmittingExchange}
            >
              {isSubmittingExchange ? 'Creating Replacement Order...' : '🚀 Spawn Replacement Order'}
            </button>
          </div>
        </Modal>
      )}

      {/* Audit Log Details Modal */}
      {selectedAuditLog && (
        <Modal
          isOpen={!!selectedAuditLog}
          onClose={() => setSelectedAuditLog(null)}
          contentClassName="batch-create-modal"
        >
          <h2>🔍 Parity Audit Breakdown: {selectedAuditLog.auditTimestamp ? new Date(selectedAuditLog.auditTimestamp).toLocaleString() : 'Audit Details'}</h2>
          <p className="modal-desc">
            Health Score: <strong>{selectedAuditLog.healthScore}%</strong> | Triggered By: <code>{selectedAuditLog.triggeredBy || 'system'}</code>
          </p>

          <div className="audit-modal-details">
            {Object.entries(selectedAuditLog.checks || {}).map(([key, data]) => (
              <div key={key} className="audit-detail-section">
                <div className="audit-detail-header">
                  <span className={`check-badge badge-${(data.status || 'passed').toLowerCase()}`}>
                    {data.status}
                  </span>
                  <h4>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}</h4>
                  <span className="anomaly-count">Anomalies: {data.count || 0}</span>
                </div>
                {data.details && data.details.length > 0 ? (
                  <pre className="audit-json-box">{JSON.stringify(data.details, null, 2)}</pre>
                ) : (
                  <p className="no-anomalies-text">✅ No anomalies detected. Parity verified.</p>
                )}
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button
              className="action-button cancel-btn"
              onClick={() => setSelectedAuditLog(null)}
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Waiting Orders Inspection Modal */}
      {selectedWaitingOrdersItem && (
        <Modal
          isOpen={!!selectedWaitingOrdersItem}
          onClose={() => setSelectedWaitingOrdersItem(null)}
          contentClassName="batch-create-modal"
        >
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '14px' }}>
            {selectedWaitingOrdersItem.imageUrl && (
              <img
                src={selectedWaitingOrdersItem.imageUrl}
                alt={selectedWaitingOrdersItem.sku}
                className="fabric-thumb-img"
                style={{ width: '64px', height: '64px', cursor: 'zoom-in', borderRadius: '8px' }}
                onClick={() => setZoomImage({ url: selectedWaitingOrdersItem.imageUrl, title: `${selectedWaitingOrdersItem.sku} - ${selectedWaitingOrdersItem.title}` })}
                title="Click to Zoom Fabric"
              />
            )}
            <div>
              <h2 style={{ margin: '0 0 4px 0' }}>📋 Open Orders Waiting for SKU: {selectedWaitingOrdersItem.sku}</h2>
              <p className="modal-desc" style={{ margin: 0 }}>
                Product: <strong>{selectedWaitingOrdersItem.title}</strong> | Shelf: <strong>{selectedWaitingOrdersItem.shelfLocation}</strong> | In Stock: <strong>{selectedWaitingOrdersItem.inStockCount}</strong>
              </p>
            </div>
          </div>

          <div className="waiting-orders-modal-list">
            {(selectedWaitingOrdersItem.orders || []).map((ord, idx) => (
              <div key={idx} className="waiting-order-card">
                <div className="waiting-order-info">
                  <span className="waiting-order-num">Order #{formatOrderNo(ord.orderNumber || ord.orderId, ord.source, ord.isCustom)}</span>
                  <span className="waiting-order-customer">👤 {ord.customerName}</span>
                  <span className="waiting-order-qty">Qty Needed: <strong>{ord.quantity}</strong></span>
                </div>
                <div className="waiting-order-actions">
                  <button
                    className="action-button primary-btn mini-btn"
                    onClick={() => {
                      handleOpenSkuAllocation(selectedWaitingOrdersItem, ord);
                      setSelectedWaitingOrdersItem(null);
                    }}
                  >
                    ⚡ Allocate to Order ➡️
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button
              className="action-button cancel-btn"
              onClick={() => setSelectedWaitingOrdersItem(null)}
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* ── SKU Piece Allocation Studio Modal ── */}
      {selectedAllocationSku && (
        <Modal
          isOpen={!!selectedAllocationSku}
          onClose={() => {
            if (isAllocatingPiece) return;
            setSelectedAllocationSku(null);
            setSelectedAllocationOrder(null);
            setAllocationFeedback(null);
          }}
          contentClassName="sku-allocation-modal"
        >
          <div className="sku-alloc-header">
            <div className="sku-alloc-header-left">
              {selectedAllocationSku.imageUrl ? (
                <img
                  src={selectedAllocationSku.imageUrl}
                  alt={selectedAllocationSku.sku}
                  className="fabric-thumb-img alloc-hero-thumb"
                  onClick={() => setZoomImage({ url: selectedAllocationSku.imageUrl, title: `${selectedAllocationSku.sku} - ${selectedAllocationSku.title}` })}
                  title="Click to Zoom Fabric"
                />
              ) : (
                <div className="fabric-thumb-placeholder alloc-hero-thumb">🧵</div>
              )}
              <div>
                <h2 className="alloc-sku-title">Allocate Stock: {selectedAllocationSku.sku}</h2>
                <div className="alloc-sku-subtitle">
                  {selectedAllocationSku.title} &bull; Shelf: <strong>{selectedAllocationSku.shelfLocation}</strong> &bull; Stock: <strong>{selectedAllocationSku.inStockCount}m</strong> &bull; Needed: <strong>{selectedAllocationSku.totalNeeded}m</strong>
                </div>
              </div>
            </div>
          </div>

          {allocationFeedback && (
            <div className={`alloc-feedback-banner ${allocationFeedback.success ? 'success' : 'error'}`}>
              {allocationFeedback.dryRun && <span className="dryrun-tag">[TEST MODE]</span>} {allocationFeedback.message}
            </div>
          )}

          <div className="sku-alloc-body-grid">
            {/* Left Column: Waiting Orders List */}
            <div className="alloc-orders-column">
              {(() => {
                const currentModalPieces = skuPieces.length > 0 ? skuPieces : (selectedAllocationSku.availablePieces || []);
                const fulfillableModalOrders = (selectedAllocationSku.orders || []).filter((ord) => {
                  const ordQty = Number(ord.quantity || 1);
                  if (ordQty <= 15) {
                    return currentModalPieces.some((p) => Number(p.length || 0) + 1e-9 >= ordQty);
                  }
                  const totalAvailable = currentModalPieces.reduce((s, p) => s + (Number(p.length) || 0), 0);
                  return totalAvailable + 1e-9 >= ordQty;
                });
                const displayOrders = fulfillableModalOrders.length > 0 ? fulfillableModalOrders : (selectedAllocationSku.orders || []);

                return (
                  <>
                    <div className="alloc-col-header">
                      <h4>Waiting Orders ({displayOrders.length})</h4>
                    </div>

                    <div className="alloc-orders-list">
                      {displayOrders.length === 0 ? (
                        <div className="alloc-empty-orders">
                          ✅ All waiting orders allocated!
                        </div>
                      ) : (
                        displayOrders.map((ord, idx) => {
                          const isSelected = (selectedAllocationOrder?.orderId || selectedAllocationOrder?.orderNumber) === (ord.orderId || ord.orderNumber);
                          const ordQty = Number(ord.quantity || 1);
                          const hasExactMatch = ordQty <= 15 && currentModalPieces.some((p) => Math.abs(Number(p.length || 0) - ordQty) < 0.01);

                          return (
                            <div
                              key={idx}
                              className={`alloc-order-item-card ${isSelected ? 'active-selected' : ''} ${hasExactMatch ? 'has-exact-match' : ''}`}
                              onClick={() => handleSelectOrderInModal(ord)}
                            >
                              <div className="alloc-order-radio">
                                <input
                                  type="radio"
                                  name="selectedOrderToAlloc"
                                  checked={isSelected}
                                  onChange={() => handleSelectOrderInModal(ord)}
                                />
                              </div>
                              <div className="alloc-order-info-main">
                                <div className="alloc-order-top-row">
                                  <strong className="alloc-order-num">#{formatOrderNo(ord.orderNumber || ord.orderId, ord.source, ord.isCustom)}</strong>
                                  <div className="alloc-order-pills-wrap">
                                    {ord.inBatch && <span className="order-in-batch-tag">🏭 IN BATCH</span>}
                                    {hasExactMatch && <span className="order-exact-pill">🎯 EXACT</span>}
                                    <span className="alloc-order-qty-pill">{ord.quantity}m</span>
                                  </div>
                                </div>
                                <div className="alloc-order-cust">{ord.customerName}</div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Right Column: Piece Selection & Cutting */}
            <div className="alloc-pieces-column">
              {selectedAllocationOrder ? (
                <>
                  <div className="alloc-active-order-banner">
                    <div className="alloc-active-target">
                      Order: <strong>#{formatOrderNo(selectedAllocationOrder.orderNumber || selectedAllocationOrder.orderId, selectedAllocationOrder.source, selectedAllocationOrder.isCustom)}</strong> ({selectedAllocationOrder.customerName})
                    </div>
                    <div className="alloc-target-req">
                      Required: <span className="alloc-highlight-qty">{Number(selectedAllocationOrder.quantity || 1).toFixed(2)}m</span>
                    </div>
                  </div>

                  {selectedAllocationOrder.inBatch && (
                    <div className="alloc-fast-track-banner">
                      🚀 <strong>In Factory Batch:</strong> Allocating this shelf piece fast-tracks the order for immediate dispatch!
                    </div>
                  )}

                  <div className="alloc-rule-badge-row">
                    {Number(selectedAllocationOrder.quantity || 1) <= 15 ? (
                      <span className="alloc-rule-badge single">
                        Continuous Piece (&le; 15m)
                      </span>
                    ) : (
                      <span className="alloc-rule-badge multi">
                        Multi-Cut Allowed (&gt; 15m)
                      </span>
                    )}
                  </div>

                  <div className="alloc-pieces-container">
                    {skuPieces.length === 0 ? (
                      <div className="alloc-no-pieces">
                        ⚠️ No active pieces registered in inventory.
                      </div>
                    ) : (() => {
                      const orderQty = Number(selectedAllocationOrder.quantity || 1);
                      const isSingleRule = orderQty <= 15;

                      if (isSingleRule) {
                        const eligiblePieces = skuPieces.filter((p) => Number(p.length || 0) + 1e-9 >= orderQty);
                        eligiblePieces.sort((a, b) => (Number(a.length || 0) - orderQty) - (Number(b.length || 0) - orderQty));
                        const shortPieces = skuPieces.filter((p) => Number(p.length || 0) + 1e-9 < orderQty);
                        shortPieces.sort((a, b) => Number(b.length || 0) - Number(a.length || 0));

                        const displayedPieces = showShortRolls
                          ? [...eligiblePieces, ...shortPieces]
                          : (eligiblePieces.length > 0 ? eligiblePieces : shortPieces);

                        return (
                          <div className="alloc-single-rule-container">
                            <div className="alloc-pieces-header">
                              <h5>Available Pieces ({eligiblePieces.length})</h5>
                              <div className="alloc-pieces-header-right">
                                {shortPieces.length > 0 && eligiblePieces.length > 0 && (
                                  <button
                                    type="button"
                                    className={`short-rolls-pill-btn ${showShortRolls ? 'active' : ''}`}
                                    onClick={() => setShowShortRolls((prev) => !prev)}
                                  >
                                    {showShortRolls ? `Hide Short (${shortPieces.length})` : `+${shortPieces.length} Short (<${orderQty}m)`}
                                  </button>
                                )}
                                {isLoadingPieces && <span className="alloc-loading-pieces">Refreshing...</span>}
                              </div>
                            </div>

                            {eligiblePieces.length === 0 && (
                              <div className="alloc-no-eligible-banner">
                                <strong>⚠️ Continuous Piece Deficit:</strong> No piece on shelf is &ge; {orderQty.toFixed(2)}m ({shortPieces.length} short remnants in stock).
                              </div>
                            )}

                            <div className="alloc-pieces-grid">
                              {displayedPieces.map((piece) => {
                                const pieceLen = Number(piece.length || 0);
                                const isShort = pieceLen + 1e-9 < orderQty;
                                const isExactMatch = !isShort && Math.abs(pieceLen - orderQty) < 0.01;
                                const isBestFit = !isShort && !isExactMatch && eligiblePieces[0]?.pieceId === piece.pieceId;
                                const isSelected = selectedPieceId === piece.pieceId;
                                const remainingAfterCut = Math.max(0, pieceLen - orderQty);

                                return (
                                  <div
                                    key={piece.pieceId}
                                    className={`alloc-piece-card ${isSelected ? 'selected' : ''} ${isExactMatch ? 'exact-match-card' : isBestFit ? 'best-fit-card' : ''} ${isShort ? 'disabled-short' : ''}`}
                                    onClick={() => {
                                      if (isShort) return;
                                      handlePieceToggle(piece);
                                    }}
                                  >
                                    <div className="alloc-piece-id-row">
                                      <span className="alloc-piece-id">Piece: <strong>{piece.pieceId}</strong></span>
                                    </div>

                                    <div className="alloc-piece-len-row">
                                      <strong>{pieceLen.toFixed(2)}m</strong> &bull; <span className="alloc-piece-shelf">{piece.shelf || 'Shelving Area'}</span>
                                    </div>

                                    {isExactMatch && (
                                      <div className="alloc-piece-badge-full exact-match-badge">
                                        🎯 EXACT MATCH
                                      </div>
                                    )}
                                    {isBestFit && (
                                      <div className="alloc-piece-badge-full best-fit-badge">
                                        ★ BEST FIT
                                      </div>
                                    )}
                                    {isShort && (
                                      <div className="alloc-piece-badge-full short-badge">
                                        SHORT ({pieceLen}m &lt; {orderQty}m)
                                      </div>
                                    )}

                                    {isSelected && !isShort && (
                                      isExactMatch ? (
                                        <div className="alloc-cut-preview exact">
                                          🎯 Exact fit &bull; 0m cutting needed
                                        </div>
                                      ) : (
                                        <div className="alloc-cut-preview">
                                          Cut {orderQty}m &rarr; {remainingAfterCut.toFixed(2)}m remaining
                                        </div>
                                      )
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }

                      // Multi-piece rule (> 15m)
                      return (
                        <div className="alloc-multi-rule-container">
                          <div className="alloc-pieces-header">
                            <h5>Available Pieces ({skuPieces.length})</h5>
                            {isLoadingPieces && <span className="alloc-loading-pieces">Refreshing...</span>}
                          </div>
                          <div className="alloc-pieces-grid">
                            {skuPieces.map((piece) => {
                              const pieceLen = Number(piece.length || 0);
                              const isSelected = Object.prototype.hasOwnProperty.call(multiPieceSelection, piece.pieceId);
                              const remainingAfterCut = Math.max(0, pieceLen - Number(multiPieceSelection[piece.pieceId] || 0));

                              return (
                                <div
                                  key={piece.pieceId}
                                  className={`alloc-piece-card ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handlePieceToggle(piece)}
                                >
                                  <div className="alloc-piece-id-row">
                                    <span className="alloc-piece-id">Piece: <strong>{piece.pieceId}</strong></span>
                                  </div>

                                  <div className="alloc-piece-len-row">
                                    <strong>{pieceLen.toFixed(2)}m</strong> &bull; <span className="alloc-piece-shelf">{piece.shelf || 'Shelving Area'}</span>
                                  </div>

                                  {isSelected ? (
                                    <div className="alloc-multi-qty-box" onClick={(e) => e.stopPropagation()}>
                                      <label>Cut meters:</label>
                                      <input
                                        type="number"
                                        step="0.1"
                                        min="0.1"
                                        max={pieceLen}
                                        value={multiPieceSelection[piece.pieceId] || ''}
                                        onChange={(e) => handleMultiPieceQtyChange(piece.pieceId, e.target.value)}
                                      />
                                      <span className="alloc-multi-rem-hint">&rarr; {remainingAfterCut.toFixed(2)}m left</span>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="alloc-confirm-footer">
                    <button
                      className={`action-submit-btn confirm-alloc-btn ${selectedAllocationOrder.inBatch ? 'fast-track-btn' : ''}`}
                      disabled={isAllocatingPiece || (!selectedPieceId && Object.keys(multiPieceSelection).length === 0)}
                      onClick={handleConfirmSkuAllocation}
                    >
                      {isAllocatingPiece
                        ? 'Allocating Stock...'
                        : selectedAllocationOrder.inBatch
                          ? `🚀 Fast-Track from Stock (In Batch) ➔ Order #${formatOrderNo(selectedAllocationOrder.orderNumber || selectedAllocationOrder.orderId, selectedAllocationOrder.source, selectedAllocationOrder.isCustom)}`
                          : `⚡ Allocate ${Number(selectedAllocationOrder.quantity || 1)}m to Order #${formatOrderNo(selectedAllocationOrder.orderNumber || selectedAllocationOrder.orderId, selectedAllocationOrder.source, selectedAllocationOrder.isCustom)}`}
                    </button>
                  </div>
                </>
              ) : (
                <div className="alloc-no-order-selected">
                  Select an order from the left list to allocate inventory.
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Deficit Waiting Orders Modal */}
      {selectedDeficitWaitingOrders && (
        <Modal
          isOpen={!!selectedDeficitWaitingOrders}
          onClose={() => setSelectedDeficitWaitingOrders(null)}
          contentClassName="batch-create-modal"
        >
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '14px' }}>
            {selectedDeficitWaitingOrders.imageUrl ? (
              <img
                src={selectedDeficitWaitingOrders.imageUrl}
                alt={selectedDeficitWaitingOrders.sku}
                className="fabric-thumb-img"
                style={{ width: '64px', height: '64px', cursor: 'zoom-in', borderRadius: '8px' }}
                onClick={() => setZoomImage({ url: selectedDeficitWaitingOrders.imageUrl, title: `${selectedDeficitWaitingOrders.sku} - ${selectedDeficitWaitingOrders.title}` })}
                title="Click to Zoom Fabric"
              />
            ) : (
              <div className="fabric-thumb-placeholder" style={{ width: '64px', height: '64px' }}>🧵</div>
            )}
            <div>
              <h2 style={{ margin: '0 0 4px 0' }}>🏭 Factory Deficit: {selectedDeficitWaitingOrders.sku}</h2>
              <p className="modal-desc" style={{ margin: 0 }}>
                Product: <strong>{selectedDeficitWaitingOrders.title}</strong> | In Stock: <strong>{selectedDeficitWaitingOrders.inStockMeters || selectedDeficitWaitingOrders.inStockCount || 0}m</strong> | Net Deficit: <strong style={{ color: '#dc2626' }}>{selectedDeficitWaitingOrders.netDeficit}m</strong>
              </p>
            </div>
          </div>

          <div className="waiting-orders-modal-list">
            {(selectedDeficitWaitingOrders.orders || []).map((ord, idx) => (
              <div key={idx} className="waiting-order-card">
                <div className="waiting-order-info">
                  <span className="waiting-order-num">Order #{formatOrderNo(ord.orderNumber || ord.orderId, ord.source, ord.isCustom)}</span>
                  <span className="waiting-order-customer">👤 {ord.customerName}</span>
                  <span className="waiting-order-qty">Meters Needed: <strong>{ord.quantity}m</strong></span>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button
              className="action-button cancel-btn"
              onClick={() => setSelectedDeficitWaitingOrders(null)}
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Fabric Lightbox Zoom Modal */}
      {zoomImage && (
        <Modal
          isOpen={!!zoomImage}
          onClose={() => setZoomImage(null)}
          contentClassName="fabric-lightbox-modal"
        >
          <div className="fabric-lightbox-header">
            <h3>🔍 Fabric Design Inspection: {zoomImage.title}</h3>
          </div>
          <div className="fabric-lightbox-body">
            <img
              src={zoomImage.url}
              alt={zoomImage.title}
              className="fabric-lightbox-fullimg"
            />
          </div>
          <div className="modal-actions" style={{ justifyContent: 'center', marginTop: '16px' }}>
            <button
              className="action-button primary-btn"
              onClick={() => setZoomImage(null)}
            >
              Close Inspection
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
